// @ts-nocheck
import { LessonPlan, TeachingModel, Quiz, Question, QuestionType, Difficulty, Presentation, Homework, HomeworkTaskType, Game, GradeLevel, GameType, SlideLayoutType, Slide, DocumentSegment, QuizSystem, QuizCategory, QuizFormat, Activity, ActivityType, ChildLearningPlan, PlanDifficulty, PlanDuration, DailyPlan, HistoryStory, LiteratureAnalysisGuide, ScienceExplanation, VideoResource, MatrixRow, SpecificationTopic, COGNITIVE_LEVELS, PresentationTheme, GenerateVideosOperation, Banner, ClassTeacherMember, ClassTeacherInvitationPreview, ClassMergeSuggestion } from '../types';

/** Theme có thể là enum (chuỗi) hoặc object từ AI — dùng cho prompt ảnh, tránh "[object Object]". */
function presentationThemeToPromptLabel(theme: Presentation['theme'] | undefined): string {
    if (theme == null) return PresentationTheme.ELEGANT;
    if (typeof theme === 'string') return theme;
    if (typeof theme === 'object' && theme !== null && 'mood' in theme && typeof (theme as { mood?: string }).mood === 'string') {
        const o = theme as { mood: string; fontStyle?: string };
        return o.fontStyle ? `${o.mood} (${o.fontStyle})` : o.mood;
    }
    return PresentationTheme.ELEGANT;
}
import { APP_MODE, API_CONFIG, API_BASE_URL, API_EDU_PREFIX, resolvePublicFileUrl, AIProvider } from "../config";

export { API_EDU_PREFIX };
import { saveUserDataToLocal, updateLocalResource } from '../utils/storageUtils';

/**
 * Mock Type for legacy schemas if any still exist
 */
const Type = {
    OBJECT: 'object',
    ARRAY: 'array',
    STRING: 'string',
    NUMBER: 'number',
    BOOLEAN: 'boolean'
};

// =================================================================================
// IMPORTANT ARCHITECTURAL NOTE
// =================================================================================
// All AI generation and data operations must go through the backend SaaS API.
// Direct calls to AI models from the frontend are disabled for security and consistency.
// =================================================================================

// Simple token cache for API calls
let EDUAPP_ACCESS_TOKEN: string | null = null;
let CURRENT_USER_ID: string | null = null;
let EDUAPP_AUTH_TYPE: 'standard' | 'student_code' | null = null;

/**
 * Chế độ KHÁCH (guest): khi bật, mọi lời gọi `${API_EDU_PREFIX}/generate/*`
 * được tự động chuyển sang `${API_EDU_PREFIX}/guest/generate/*` (không cần auth,
 * rate-limit theo IP). Bật khi mount creator ở /thu-tao/* (GuestCreatorHost).
 */
let EDU_GUEST_MODE = false;
export const setEduGuestMode = (on: boolean) => { EDU_GUEST_MODE = on; };
export const isEduGuestMode = () => EDU_GUEST_MODE;

export const setEduappAccessToken = (token: string | null, userId: string | null = null, authType: 'standard' | 'student_code' | null = null) => { 
    EDUAPP_ACCESS_TOKEN = token; 
    CURRENT_USER_ID = userId ?? null;
    EDUAPP_AUTH_TYPE = authType ?? (token ? 'standard' : null);
    persistAuth(); 
};

export const getEduappAuthType = (): 'standard' | 'student_code' | null => EDUAPP_AUTH_TYPE;
export const EDUAPP_CREDIT_BALANCE_UPDATED_EVENT = 'eduapp-credit-balance-updated';

const emitCreditBalanceUpdated = (balance: number, source: string) => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(EDUAPP_CREDIT_BALANCE_UPDATED_EVENT, {
        detail: { balance, source }
    }));
};

const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        const payload = await response.json().catch(() => ({}));
        const message = payload?.error || payload?.message;
        return typeof message === 'string' && message.trim() ? message : fallback;
    }

    const text = await response.text().catch(() => '');
    const compact = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return compact ? compact.slice(0, 180) : fallback;
};

export const refreshCreditBalance = async (source: string): Promise<number | null> => {
    if (APP_MODE !== 'PRODUCTION' || !EDUAPP_ACCESS_TOKEN || EDUAPP_AUTH_TYPE === 'student_code') {
        return null;
    }
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/credits/balance`, { method: 'GET' });
        if (!res.ok) return null;
        const data = await res.json().catch(() => ({}));
        const balance = Number((data as { balance?: number }).balance);
        if (Number.isFinite(balance)) {
            emitCreditBalanceUpdated(balance, source);
            return balance;
        }
    } catch (error) {
        console.warn('Unable to refresh credit balance:', error);
    }
    return null;
};

/** Tải ảnh/video đính kèm cho bài nộp nhiệm vụ. Trả về { url, type, name }. */
export const uploadSubmissionAttachment = async (file: File): Promise<{ url: string; type: string; name: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(apiUrl(`${API_EDU_PREFIX}/submissions/upload-attachment`), {
        method: 'POST',
        headers: { 'Accept': 'application/json', ...authHeader() } as Record<string, string>,
        body: formData,
        credentials: 'include',
    });
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();
    return { url: formatAssetUrl(data.url) || data.url, type: data.type || 'image', name: data.name || file.name };
};

/** Tải tài liệu thư viện lên (PDF, Word, TXT...). */
export const uploadLibraryDocument = async (file: File, onProgress?: (pct: number) => void): Promise<any> => {
    const res = await uploadFileWithProgress(`${API_EDU_PREFIX}/library/upload-document`, file, onProgress);
    return res;
};

/** Tải hình ảnh thư viện lên. */
export const uploadLibraryImage = async (file: File, onProgress?: (pct: number) => void): Promise<any> => {
    const res = await uploadFileWithProgress(`${API_EDU_PREFIX}/library/upload-image`, file, onProgress);
    return res;
};

/** Tải video thư viện lên. */
export const uploadLibraryVideo = async (file: File, onProgress?: (pct: number) => void): Promise<any> => {
    const res = await uploadFileWithProgress(`${API_EDU_PREFIX}/library/upload-video`, file, onProgress);
    return res;
};

/** Tải audio thư viện lên (sử dụng chung route video của backend). */
export const uploadLibraryAudio = async (file: File, onProgress?: (pct: number) => void): Promise<any> => {
    const res = await uploadFileWithProgress(`${API_EDU_PREFIX}/library/upload-video`, file, onProgress);
    return res;
};

/** Helper to upload file with progress using XMLHttpRequest */
const uploadFileWithProgress = (path: string, file: File, onProgress?: (pct: number) => void): Promise<any> => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        formData.append('file', file);

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
                const pct = Math.round((e.loaded * 100) / e.total);
                onProgress(pct);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText));
                } catch {
                    resolve(xhr.responseText);
                }
            } else {
                let errorMsg = 'Lỗi tải lên';
                try {
                    const errorData = JSON.parse(xhr.responseText);
                    errorMsg = errorData.message || errorMsg;
                } catch {}
                reject(new Error(errorMsg));
            }
        });

        xhr.addEventListener('error', () => reject(new Error('Lỗi kết nối mạng')));
        xhr.addEventListener('abort', () => reject(new Error('Tải lên bị hủy')));

        xhr.open('POST', apiUrl(path));
        
        // Add headers
        xhr.setRequestHeader('Accept', 'application/json');
        const auth = authHeader() as Record<string, string>;
        if (auth['Authorization']) {
            xhr.setRequestHeader('Authorization', auth['Authorization']);
        }
        
        xhr.withCredentials = true;
        xhr.send(formData);
    });
};

/** Tổng hợp nhiều tài liệu thư viện thành một. */
export const combineLibraryDocuments = async (ids: string[], name: string): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/library/combine-documents`, {
        method: 'POST',
        body: JSON.stringify({ ids, name }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Lỗi tổng hợp' }));
        throw new Error(err.message || 'Lỗi tổng hợp');
    }
    return await res.json();
};

/** Nộp bài / xác nhận hoàn thành (hỗ trợ cả parent và student_code). Trả về { id } khi thành công. */
export const submitStudentWork = async (assignmentId: string, studentId: string, submittedDate: string, content?: { answers?: Record<string, any>; attachments?: { url: string; type: string; name: string }[] }): Promise<{ id?: string | number } | null> => {
    if (APP_MODE !== 'PRODUCTION' && !EDUAPP_ACCESS_TOKEN) return null;
    const isStudentCode = EDUAPP_AUTH_TYPE === 'student_code';
    const payload: Record<string, any> = { assignment_id: assignmentId, student_id: String(studentId), status: 'da-nop', submitted_date: submittedDate };
    if (content && (content.attachments?.length || Object.keys(content.answers || {}).length)) {
        payload.content = content;
    }
    const path = isStudentCode ? `${API_EDU_PREFIX}/student/submit` : `${API_EDU_PREFIX}/submissions`;
    const res = await apiFetch(path, { method: 'POST', body: JSON.stringify(payload) });
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();
    return data?.id != null ? { id: data.id } : data;
};

/** Lấy chi tiết một bài tập/nhiệm vụ (hỗ trợ cả student_code). */
export const fetchAssignmentById = async (id: string | number): Promise<any | null> => {
    if (APP_MODE !== 'PRODUCTION') return null;
    try {
        const isStudentCode = EDUAPP_AUTH_TYPE === 'student_code';
        const path = isStudentCode ? `${API_EDU_PREFIX}/student/assignments/${id}` : `${API_EDU_PREFIX}/assignments/${id}`;
        const response = await apiFetch(path);
        if (!response.ok) return null;
        return await response.json();
    } catch (error) {
        console.error(`Error fetching assignment with id ${id}:`, error);
        return null;
    }
};

/** Chấm bài tự động bằng AI (Giáo viên). Trả về gợi ý điểm và nhận xét. */
export const aiGradeSubmissionApi = async (submissionId: string | number): Promise<{ 
    suggestedGrade: number; 
    teacherComments: string; 
    taskResults?: Record<string, { isCorrect: boolean; feedback: string }>;
    creditsDeducted?: number;
}> => {
    if (APP_MODE !== 'PRODUCTION') {
        return { 
            suggestedGrade: 8.5, 
            teacherComments: "[MOCK AI] Bài làm khá tốt, nội dung mạch lạc và bám sát yêu cầu. Tuy nhiên cần chú ý hơn về cách diễn đạt ở câu cuối.",
            creditsDeducted: 10
        };
    }
    const res = await apiFetch(`${API_EDU_PREFIX}/submissions/${submissionId}/ai-grade`, { method: 'POST' });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Lỗi chấm bài AI' }));
        throw new Error(err.error || 'Lỗi chấm bài AI');
    }
    const data = await res.json();
    await refreshCreditBalance('ai-grade');
    return data;
};

// =================================================================================
// QUIZ ATTEMPTS — Phase 2 (S2 — làm đề online + AI chấm; S4 — weak topics)
// =================================================================================

export interface QuizAttemptSnapshot {
    title?: string;
    subject?: string;
    gradeLevel?: string;
    classLevel?: string;
    duration?: number | null;
    audience?: 'teacher' | 'parent' | 'student' | null;
    purposePreset?: string | null;
    totalQuestions?: number;
    shuffleQuestions?: boolean;
    shuffleOptions?: boolean;
    showAnswerAfterSubmit?: boolean;
    timeLimit?: number | null;
}

export interface QuizAttemptBreakdownEntry {
    isCorrect: boolean;
    score: number;
    max: number;
    feedback: string;
    type?: string;
    topic?: string | null;
    cognitive?: 'NB' | 'TH' | 'VD' | 'VDC';
    correctAnswer?: any;
    modelAnswer?: string;
    aiGraded?: boolean;
    pendingAi?: boolean;
    /** Nội dung câu hỏi gốc — để hiện lại khi xem kết quả, không chỉ điểm số. */
    questionText?: string;
    /** Danh sách phương án (Trắc nghiệm) — dùng để đối chiếu cùng correctAnswer/userAnswer. */
    options?: string[] | null;
    /** Đáp án học sinh đã chọn/nhập lúc làm bài. */
    userAnswer?: any;
    /** Giải thích ngắn cho đáp án đúng (sinh sẵn lúc tạo đề). */
    explanation?: string | null;
    /** Lời giải chi tiết (nếu có) — sâu hơn `explanation`. */
    detailedSolution?: string | null;
}

export interface QuizAttemptDto {
    id: string;
    quizId: string;
    childId?: string | null;
    status: 'in_progress' | 'submitted' | 'graded';
    score: number | null;
    maxScore: number | null;
    correctCount: number | null;
    totalCount: number | null;
    startedAt: string | null;
    submittedAt: string | null;
    gradedAt: string | null;
    timeSpentSeconds: number | null;
    snapshotMeta: QuizAttemptSnapshot | null;
    answers?: Record<string, any>;
    breakdown?: {
        perQuestion: Record<string, QuizAttemptBreakdownEntry>;
        byCognitive: Record<'NB' | 'TH' | 'VD' | 'VDC', { correct: number; total: number; score: number; max: number }>;
        byTopic: Record<string, { correct: number; total: number; score: number; max: number }>;
        totals: { score: number; max: number; correct: number; total: number; percentage: number; score10: number };
    };
    aiFeedback?: string;
}

export interface QuizForTaker {
    id: string;
    title?: string;
    subject?: string;
    gradeLevel?: string;
    classLevel?: string;
    duration?: number | null;
    audience?: string | null;
    purposePreset?: string | null;
    passages?: any[];
    questions: any[];
    onlineMode: {
        enabled: boolean;
        showAnswerAfterSubmit: boolean;
        shuffleQuestions: boolean;
        shuffleOptions: boolean;
        allowRetake: boolean;
        timeLimit?: number | null;
    };
    sourceMode?: 'ai' | 'document' | 'image' | null;
    /** Ảnh GỐC học sinh đã tải lên khi sinh đề từ ảnh (sourceMode='image') — hiện lại để tham chiếu khi làm bài. */
    sourceImages?: { id: number; name: string | null; url: string | null }[];
}

/** Bắt đầu 1 lượt làm đề online. Trả về attempt + quiz đã lọc đáp án. */
export const startQuizAttempt = async (quizId: string, childId?: string | null): Promise<{ attempt: QuizAttemptDto; quiz: QuizForTaker }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/quiz-attempts/start`, {
        method: 'POST',
        body: JSON.stringify({ quizId, childId: childId || undefined }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Không thể bắt đầu làm bài' }));
        throw new Error(err.error || 'Không thể bắt đầu làm bài');
    }
    return res.json();
};

/** Lưu nháp tự động (autosave) trong khi làm bài. */
export const autosaveQuizAttempt = async (attemptId: string, answers: Record<string, any>, timeSpentSeconds?: number): Promise<{ ok: boolean; savedAt: string }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/quiz-attempts/${attemptId}/autosave`, {
        method: 'PATCH',
        body: JSON.stringify({ answers, timeSpentSeconds }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Lỗi autosave' }));
        throw new Error(err.error || 'Lỗi autosave');
    }
    return res.json();
};

/** Nộp bài — backend chấm tự động (MCQ/Numeric/Fill/Match/Order) + AI chấm tự luận. */
export const submitQuizAttempt = async (attemptId: string, answers: Record<string, any>, timeSpentSeconds?: number): Promise<{ attempt: QuizAttemptDto; creditsDeducted: number }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/quiz-attempts/${attemptId}/submit`, {
        method: 'POST',
        body: JSON.stringify({ answers, timeSpentSeconds }),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Lỗi nộp bài' }));
        throw new Error(err.error || 'Lỗi nộp bài');
    }
    const data = await res.json();
    await refreshCreditBalance('quiz-attempt-submit');
    return data;
};

/** Xem chi tiết 1 attempt. */
export const getQuizAttempt = async (attemptId: string): Promise<QuizAttemptDto> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/quiz-attempts/${attemptId}`, { method: 'GET' });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Không tìm thấy' }));
        throw new Error(err.error || 'Không tìm thấy');
    }
    const data = await res.json();
    return data.attempt;
};

/**
 * Bật chế độ chia sẻ cho 1 lượt làm bài (QuizAttempt).
 * Trả về shareUrl dạng /chia-se/ket-qua-thi/slug-iID?token=...
 */
export const shareQuizAttempt = async (attemptId: string, visibility: 'link' | 'public' = 'link'): Promise<{ ok: boolean; shareUrl: string; shareToken: string }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/quiz-attempts/${attemptId}/share`, {
        method: 'POST',
        body: JSON.stringify({ visibility })
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Lỗi chia sẻ' }));
        throw new Error(err.error || 'Lỗi chia sẻ');
    }
    return await res.json();
};

/** Liệt kê attempts của user (có thể filter theo quizId hoặc childId). */
export const listQuizAttempts = async (filters?: { quizId?: string; childId?: string; status?: string }): Promise<QuizAttemptDto[]> => {
    const params = new URLSearchParams();
    if (filters?.quizId) params.set('quizId', filters.quizId);
    if (filters?.childId) params.set('childId', filters.childId);
    if (filters?.status) params.set('status', filters.status);
    const qs = params.toString();
    const res = await apiFetch(`${API_EDU_PREFIX}/quiz-attempts${qs ? `?${qs}` : ''}`, { method: 'GET' });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.data) ? data.data : [];
};

/* ─────────────────────────────────────────────────────────────────────────────
   Parent Progress API — dữ liệu học tập con từ phía giáo viên
───────────────────────────────────────────────────────────────────────────── */
export interface ChildSubmissionDto {
    id: string;
    assignmentId: string;
    status: string;
    grade: number | null;
    submittedDate: string | null;
    feedback: string | null;
    content?: any;
}

export interface ChildAssignmentDto {
    id: string;
    title: string;
    type: string;
    dueDate: string | null;
    assignedDate: string | null;
    scheduledDate?: string | null;
    classId: string;
    homeworkId?: string | null;
    instructions?: string | null;
    submissionType?: string | null;
    gradingSystem?: string | null;
    status?: string | null;
    isForStudent?: boolean;
    submitted: boolean;
    grade: number | null;
    feedback?: string | null;
    submissionStatus: string;
}

export interface ChildSessionDto {
    id: string;
    classId: string;
    className: string;
    title: string;
    date: string | null;
    grade: number | string | null;
    attendance: string | null;
    comments: string | null;
}

export interface ChildLearningPlanDto {
    id: string;
    childId: string;
    title: string;
    goal: string;
    dailyPlans: any[];
    difficulty: string;
    duration: string;
    planStatus: string;
    createdAt: string | null;
}

export interface ChildProgressDto {
    quiz_attempts: QuizAttemptDto[];
    submissions: ChildSubmissionDto[];
    assignments: ChildAssignmentDto[];
    sessions: ChildSessionDto[];
    learning_plans: ChildLearningPlanDto[];
    student_info: { id: string; name: string; studentCode: string | null; classIds: string[]; classNames?: Record<string, string> } | null;
    linked: boolean;
    message?: string;
}

/** Lấy dữ liệu học tập tổng hợp của con (quiz + submissions + assignments từ GV). */
export const getChildProgress = async (childId: string): Promise<ChildProgressDto> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/parent/child/${childId}/progress`, { method: 'GET' });
    if (!res.ok) {
        return { quiz_attempts: [], submissions: [], assignments: [], sessions: [], learning_plans: [], student_info: null, linked: false, message: 'Không thể tải dữ liệu' };
    }
    return await res.json();
};

/**
 * Lộ trình học ĐỒNG NHẤT cho tài khoản học sinh: gồm lộ trình của chính học
 * sinh (user_id) + lộ trình phụ huynh tạo cho con đã liên kết (child_id),
 * đã merge tiến độ module. Trả về mảng ChildLearningPlan dùng chung với app.
 */
export const getUnifiedLearningPlans = async (): Promise<ChildLearningPlan[]> => {
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/user/learning/unified`, { method: 'GET' });
        if (!res.ok) return [];
        const data = await res.json();
        return (data.learning_plans ?? []) as ChildLearningPlan[];
    } catch {
        return [];
    }
};

// ── Khoe thành tích con (viral share card) ──────────────────────────────────
export interface AchievementCardDto {
    token: string;
    type: string;
    childName: string;
    title: string;
    subtitle: string | null;
    value: string | null;
    emoji: string | null;
    accent: string;
    views: number;
    createdAt: string | null;
}
export interface ShareAchievementInput {
    type?: string;
    childId?: string | number | null;
    childName: string;
    title: string;
    subtitle?: string | null;
    value?: string | null;
    emoji?: string | null;
    accent?: string;
}

/** Tạo thẻ khoe thành tích con → trả token + link landing công khai. */
export const shareAchievement = async (
    input: ShareAchievementInput,
): Promise<{ token: string; shareUrl: string; card: AchievementCardDto } | null> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/parent/achievement/share`, {
        method: 'POST',
        body: JSON.stringify({
            type: input.type ?? 'custom',
            child_id: input.childId ?? null,
            child_name: input.childName,
            title: input.title,
            subtitle: input.subtitle ?? null,
            value: input.value ?? null,
            emoji: input.emoji ?? null,
            accent: input.accent ?? 'indigo',
        }),
    });
    if (!res.ok) return null;
    return await res.json();
};

/** Lấy thẻ thành tích công khai (landing, no auth) — tự đếm view. */
export const getSharedAchievement = async (token: string): Promise<AchievementCardDto | null> => {
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/public/achievement/${encodeURIComponent(token)}`, { method: 'GET' });
        if (!res.ok) return null;
        const data = await res.json();
        return data.card as AchievementCardDto;
    } catch {
        return null;
    }
};

/** Đếm lượt bấm CTA "Dùng thử" từ landing (đo chuyển đổi viral). */
export const trackAchievementCta = async (token: string): Promise<void> => {
    try {
        await apiFetch(`${API_EDU_PREFIX}/public/achievement/${encodeURIComponent(token)}/cta`, { method: 'POST' });
    } catch { /* fire-and-forget */ }
};

// ── Vòng mời chéo (viral invite) ────────────────────────────────────────────
export type InviteType = 'teacher' | 'parent' | 'general';
export interface InviteDto {
    token: string;
    type: InviteType;
    inviterName: string;
    childName: string | null;
    message: string | null;
    views: number;
    createdAt: string | null;
}

/** Tạo link mời cá nhân (giáo viên/phụ huynh) → trả token + link landing. */
export const createInvite = async (
    input: { type?: InviteType; childName?: string | null; message?: string | null },
): Promise<{ token: string; inviteUrl: string; invite: InviteDto } | null> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/invite/create`, {
        method: 'POST',
        body: JSON.stringify({
            type: input.type ?? 'general',
            child_name: input.childName ?? null,
            message: input.message ?? null,
        }),
    });
    if (!res.ok) return null;
    return await res.json();
};

/** Lấy lời mời công khai (landing, no auth) — tự đếm view. */
export const getInvite = async (token: string): Promise<InviteDto | null> => {
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/public/invite/${encodeURIComponent(token)}`, { method: 'GET' });
        if (!res.ok) return null;
        const data = await res.json();
        return data.invite as InviteDto;
    } catch {
        return null;
    }
};

/** Đếm lượt bấm CTA đăng ký từ landing lời mời. */
export const trackInviteCta = async (token: string): Promise<void> => {
    try {
        await apiFetch(`${API_EDU_PREFIX}/public/invite/${encodeURIComponent(token)}/cta`, { method: 'POST' });
    } catch { /* fire-and-forget */ }
};

export interface WeakTopicDto {
    id: number;
    topic: string;
    subject: string;
    classLevel?: string | null;
    gradeLevel?: string | null;
    mastery: number;
    attempts: number;
    correct: number;
    quizzesCount: number;
    lastSeenAt?: string | null;
}

export interface MasterySummaryDto {
    topicsCount: number;
    weakCount: number;
    avgMastery: number | null;
    subjects: Array<{ subject: string; topics: number; avgMastery: number; weak: number }>;
}

/** Lấy danh sách top weak topics + tổng kết mastery của user (HS / PH cho con). */
export const getStudentWeakTopics = async (params?: { subject?: string; classLevel?: string; limit?: number }): Promise<{ weakTopics: WeakTopicDto[]; summary: MasterySummaryDto }> => {
    const qs = new URLSearchParams();
    if (params?.subject) qs.set('subject', params.subject);
    if (params?.classLevel) qs.set('classLevel', params.classLevel);
    if (params?.limit) qs.set('limit', String(params.limit));
    const url = `${API_EDU_PREFIX}/student/weak-topics${qs.toString() ? `?${qs}` : ''}`;
    const res = await apiFetch(url, { method: 'GET' });
    if (!res.ok) {
        return { weakTopics: [], summary: { topicsCount: 0, weakCount: 0, avgMastery: null, subjects: [] } };
    }
    return res.json();
};

/** Đăng xuất tất cả thiết bị (student code login). */
export const studentLogoutAll = async (): Promise<void> => {
  if (APP_MODE !== 'PRODUCTION' || EDUAPP_AUTH_TYPE !== 'student_code') return;
  await apiFetch(`${API_EDU_PREFIX}/student/logout-all`, { method: 'POST' });
};

const AUTH_STORAGE_KEY = 'EDUAPP_AUTH';

const loadAuthFromStorage = () => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    EDUAPP_ACCESS_TOKEN = parsed.accessToken || null;
    EDUAPP_AUTH_TYPE = parsed.authType === 'student_code' ? 'student_code' : (parsed.accessToken ? 'standard' : null);
    // legacy fields ignored: provider, providerId, providerToken
  } catch {}
};

const persistAuth = () => {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ 
      accessToken: EDUAPP_ACCESS_TOKEN, 
      authType: EDUAPP_AUTH_TYPE 
    }));
  } catch {}
};

loadAuthFromStorage();

const authHeader = () => EDUAPP_ACCESS_TOKEN ? { 'Authorization': `Bearer ${EDUAPP_ACCESS_TOKEN}` } : {};

// Centralized fetch wrapper for backend API
const apiUrl = (path: string) => `${API_BASE_URL}${path}`;
const tryRefreshToken = async (): Promise<boolean> => {
  try {
    const res = await fetch(`${API_BASE_URL}${API_EDU_PREFIX}/auth/refresh`, { 
        method: 'POST', 
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.access_token) {
      setEduappAccessToken(data.access_token);
      return true;
    }
  } catch {}
  return false;
};

export const apiFetch = async (path: string, init?: RequestInit) => {
    const baseHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(init?.headers || {})
    };
    const headers = { ...baseHeaders, ...authHeader() } as Record<string, string>;

    // Chế độ khách: định tuyến generate → guest/generate (không auth, rate-limit IP).
    if (EDU_GUEST_MODE && !EDUAPP_ACCESS_TOKEN) {
        const genPrefix = `${API_EDU_PREFIX}/generate/`;
        if (path.startsWith(genPrefix)) {
            path = `${API_EDU_PREFIX}/guest/generate/` + path.slice(genPrefix.length);
        }
    }

    const url = apiUrl(path);
    const res = await fetch(url, { ...init, headers, credentials: 'include' });
    // Detect free-tier daily limit and surface upgrade modal globally
    if (res.status === 429) {
        try {
            const clone = res.clone();
            const data = await clone.json();
            if (data?.upgrade_required) {
                window.dispatchEvent(new CustomEvent('eduapp-upgrade-required', {
                    detail: { message: data.message, reset_at: data.reset_at, limit: data.limit, used: data.used }
                }));
            }
        } catch (_) { /* ignore parse errors */ }
    }
    // Hết credits (HTTP 402) → phát sự kiện toàn cục để App.tsx mở OutOfCreditsModal.
    // Clone response trước để caller vẫn có thể đọc body gốc (ví dụ lấy message lỗi).
    if (res.status === 402) {
        try {
            const clone = res.clone();
            const data = await clone.json().catch(() => ({}));
            // reset_at/reason/error_code chỉ có khi backend chạy pricing_enforcement_mode='plan'
            // (xem GenerateController::insufficientCreditsResponse) — undefined ở chế độ legacy,
            // modal tự fallback về wording chung như trước.
            window.dispatchEvent(new CustomEvent('eduapp-out-of-credits', {
                detail: {
                    message: (data as any)?.message ?? 'Hết lượt sử dụng (Credits)',
                    resetAt: (data as any)?.reset_at ?? undefined,
                    reason: (data as any)?.reason ?? undefined,
                    errorCode: (data as any)?.error_code ?? undefined,
                }
            }));
        } catch (_) { /* ignore */ }
    }
    if (res.status !== 401) return res;
    // Khách (không có token): không refresh/không bắn logout — trả nguyên response.
    if (!EDUAPP_ACCESS_TOKEN) return res;
    // Student code login không có refresh token
    if (EDUAPP_AUTH_TYPE === 'student_code') {
        window.dispatchEvent(new CustomEvent('eduapp-logout', { detail: { reason: 'session_expired' } }));
        return res;
    }
    // Attempt refresh once
    const refreshed = await tryRefreshToken();
    if (!refreshed) {
        window.dispatchEvent(new CustomEvent('eduapp-logout', { detail: { reason: 'session_expired' } }));
        return res;
    }
    const retryHeaders = { ...baseHeaders, ...authHeader() } as Record<string, string>;
    const retryRes = await fetch(apiUrl(path), { ...init, headers: retryHeaders, credentials: 'include' });
    if (retryRes.status === 401) {
        window.dispatchEvent(new CustomEvent('eduapp-logout', { detail: { reason: 'session_expired' } }));
    }
    return retryRes;
};

// Join Requests
export const getClassUpdates = async (classId: string | number): Promise<{ class: any; joinRequests: any[]; students: any[] } | null> => {
    if (APP_MODE !== 'PRODUCTION') return null;
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/updates`);
        if (!res.ok) return null;
        const data = await res.json();
        return data;
    } catch { return null; }
};

export const getJoinRequests = async (): Promise<any[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/join-requests`);
        if (!res.ok) return [];
        const data = await res.json();
        return data.requests || [];
    } catch { return []; }
};

/** Kiểm tra sớm: con đã trong lớp / có yêu cầu chờ chưa (trước khi hiện form xác nhận) */
export const checkJoinEligibility = async (classId: string | number, childId: string | number, studentId?: string | number): Promise<{ canJoin: boolean; message?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { canJoin: true };
    try {
        const params = new URLSearchParams({ class_id: String(classId), child_id: String(childId) });
        if (studentId) params.set('student_id', String(studentId));
        const res = await apiFetch(`${API_EDU_PREFIX}/join-requests/check?${params}`);
        const data = await res.json();
        return { canJoin: !!data.can_join, message: data.message };
    } catch { return { canJoin: true }; }
};

export const createJoinRequest = async (payload: {
    child_id?: string|number;
    class_id: string|number;
    student_id?: string|number;
    message?: string;
    is_student_self_join?: boolean;
    parent_info?: { name?: string; phone?: string; relationship?: string; email?: string };
    student_info?: { name?: string; real_name?: string; dob?: string; gender?: string };
}): Promise<{ success: boolean; isDuplicate?: boolean; message?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { success: true };
    const isStudentCode = EDUAPP_AUTH_TYPE === 'student_code';
    if (isStudentCode) {
        return {
            success: false,
            message: 'Học sinh đăng nhập bằng mã được gán sẵn vào lớp. Chức năng tham gia lớp khác không áp dụng.',
        };
    }
    const path = `${API_EDU_PREFIX}/join-requests`;
    const body: Record<string, unknown> = { ...payload };
    if (payload.is_student_self_join) {
        body.is_student_self_join = 1;
        delete body.child_id;
    }
    if (payload.parent_info) body.parent_info = payload.parent_info;
    if (payload.student_info) body.student_info = payload.student_info;
    const res = await apiFetch(path, {
        method: 'POST',
        body: JSON.stringify(body)
    });
    if (res.ok) return { success: true };
    const bodyText = await res.text();
    let parsed: { message?: string; code?: string } = {};
    try {
        parsed = bodyText ? JSON.parse(bodyText) : {};
    } catch { /* ignore */ }
    const msg = (parsed.message || parsed.error || '').toString().toLowerCase();
    const fullText = bodyText.toLowerCase();
    const code = parsed.code || '';
    // Đã trong lớp hoặc đang chờ: trả về message thân thiện, coi như không lỗi nghiêm trọng
    if (res.status === 422) {
        if (code === 'already_in_class' || code === 'pending_exists') {
            return { success: true, isDuplicate: true, message: parsed.message };
        }
        const isDuplicateMessage = msg.includes('đã được gửi') || msg.includes('đang chờ') ||
            msg.includes('thành viên') || fullText.includes('đã được gửi') || fullText.includes('đang chờ');
        if (isDuplicateMessage) {
            return { success: true, isDuplicate: true, message: parsed.message };
        }
    }
    return { success: false, message: parsed.message };
};

export const approveJoinRequestApi = async (id: string|number): Promise<boolean> => {
    if (APP_MODE !== 'PRODUCTION') return true;
    const res = await apiFetch(`${API_EDU_PREFIX}/join-requests/${id}/approve`, { method: 'POST' });
    return res.ok;
};

export const rejectJoinRequestApi = async (id: string|number, rejectReason?: string): Promise<boolean> => {
    if (APP_MODE !== 'PRODUCTION') return true;
    const res = await apiFetch(`${API_EDU_PREFIX}/join-requests/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reject_reason: rejectReason || '' }),
    });
    return res.ok;
};

/** Hủy liên kết tài khoản phụ huynh với học sinh (GV thực hiện) */
export const unlinkParentFromStudentApi = async (classId: string|number, studentId: string|number, parentUserId: number, reason?: string): Promise<{ success: boolean; message?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { success: true };
    const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students/${studentId}/unlink-parent`, {
        method: 'POST',
        body: JSON.stringify({ parent_user_id: parentUserId, reason }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, message: (data as any).error || 'Không thể hủy liên kết' };
    return { success: true };
};

/** Hủy liên kết tài khoản học sinh với hồ sơ (GV thực hiện) */
export const unlinkStudentAccountApi = async (classId: string|number, studentId: string|number, reason?: string): Promise<{ success: boolean; message?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { success: true };
    const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students/${studentId}/unlink-student-account`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, message: (data as any).error || 'Không thể hủy liên kết' };
    return { success: true };
};

// =================================================================================
// Lớp nhiều giáo viên (GVCN + GV bộ môn dùng chung roster) — Multi-teacher class
// =================================================================================

/** Danh sách GV đang active của 1 lớp (bất kỳ GV nào của lớp cũng gọi được) */
export const listClassTeachersApi = async (classId: string | number): Promise<ClassTeacherMember[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/teachers`);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.teachers) ? data.teachers : [];
    } catch { return []; }
};

/** Mời 1 đồng nghiệp làm GV của lớp (chỉ permission=manage). Trả token link mời khi thành công. */
export const inviteClassTeacherApi = async (
    classId: string | number,
    payload: { email?: string; role?: string; subject?: string; permission_level?: string }
): Promise<{ token: string; invitation?: any } | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { token: 'demo-token' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/teachers/invite`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể tạo lời mời.' };
        return { token: (data as any).token, invitation: (data as any).invitation };
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi tạo lời mời.' };
    }
};

/** Đổi vai trò/môn/quyền của 1 đồng GV trong lớp (chỉ permission=manage) */
export const updateClassTeacherApi = async (
    classId: string | number,
    teacherId: string | number,
    payload: { role?: string; subject?: string; permission_level?: string }
): Promise<{ updated: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { updated: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/teachers/${teacherId}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { updated: false, error: (data as any)?.error || (data as any)?.message || 'Không thể cập nhật.' };
        return { updated: true };
    } catch (e: any) {
        return { updated: false, error: e?.message || 'Lỗi khi cập nhật.' };
    }
};

/** Gỡ 1 đồng GV khỏi lớp (chỉ permission=manage; lỗi nếu là primary owner hoặc manage cuối cùng) */
export const removeClassTeacherApi = async (
    classId: string | number,
    teacherId: string | number
): Promise<{ removed: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { removed: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/teachers/${teacherId}`, {
            method: 'DELETE',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { removed: false, error: (data as any)?.error || (data as any)?.message || 'Không thể gỡ giáo viên.' };
        return { removed: true };
    } catch (e: any) {
        return { removed: false, error: e?.message || 'Lỗi khi gỡ giáo viên.' };
    }
};

/** GV tự rời lớp (lỗi nếu là primary owner hoặc manage cuối cùng) */
export const leaveClassApi = async (classId: string | number): Promise<{ left: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { left: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/leave`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { left: false, error: (data as any)?.error || (data as any)?.message || 'Không thể rời lớp.' };
        return { left: true };
    } catch (e: any) {
        return { left: false, error: e?.message || 'Lỗi khi rời lớp.' };
    }
};

/** Xem trước thông tin lời mời đồng GV theo token (GV bất kỳ đã đăng nhập) */
export const previewTeacherInvitationApi = async (
    token: string
): Promise<{ invitation: ClassTeacherInvitationPreview } | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') {
        return { invitation: { className: 'Lớp mẫu', inviterName: 'GV mẫu', role: 'subject_teacher', subject: 'Toán', permissionLevel: 'teach', studentCount: 0, expiresAt: null } };
    }
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/teacher-invitations/${token}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            if (res.status === 404) return { error: 'Không tìm thấy lời mời này.' };
            if (res.status === 410) return { error: 'Lời mời đã hết hạn hoặc đã được sử dụng.' };
            return { error: (data as any)?.error || (data as any)?.message || 'Không thể tải thông tin lời mời.' };
        }
        return { invitation: (data as any).invitation };
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi tải thông tin lời mời.' };
    }
};

/** Chấp nhận lời mời đồng GV — tham gia lớp với vai trò/quyền đã được mời */
export const acceptTeacherInvitationApi = async (token: string): Promise<{ joined: boolean; classId?: string; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { joined: true, classId: '1' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/teacher-invitations/${token}/accept`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            if (res.status === 404) return { joined: false, error: 'Không tìm thấy lời mời này.' };
            if (res.status === 410) return { joined: false, error: 'Lời mời đã hết hạn hoặc đã được sử dụng.' };
            return { joined: false, error: (data as any)?.error || (data as any)?.message || 'Không thể tham gia lớp.' };
        }
        return { joined: true, classId: (data as any).classId != null ? String((data as any).classId) : undefined };
    } catch (e: any) {
        return { joined: false, error: e?.message || 'Lỗi khi tham gia lớp.' };
    }
};

/** Từ chối lời mời đồng GV */
export const declineTeacherInvitationApi = async (token: string): Promise<{ declined: boolean }> => {
    if (APP_MODE !== 'PRODUCTION') return { declined: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/teacher-invitations/${token}/decline`, { method: 'POST' });
        return { declined: res.ok };
    } catch { return { declined: false }; }
};

// =================================================================================
// Class Merge Tool — xử lý lớp trùng lặp (gộp roster 2 lớp do cùng GV sở hữu chính)
// =================================================================================

/** Gợi ý các lớp trùng tên/khối/năm học trong số lớp GV đang thao tác sở hữu chính (permission=manage trên {classId}) */
export const getMergeSuggestionsApi = async (classId: string | number): Promise<ClassMergeSuggestion[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/merge-suggestions`, { method: 'POST' });
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.suggestions) ? data.suggestions : [];
    } catch { return []; }
};

/**
 * Gộp roster lớp nguồn (source_class_id) vào lớp đích (classId). `mapping` phải bao gồm
 * TẤT CẢ học sinh của lớp nguồn — target_student_id=null nghĩa là "giữ riêng" (chuyển
 * sang lớp đích, không gộp hồ sơ); có giá trị nghĩa là gộp hồ sơ nguồn vào học sinh đích đó.
 */
export const mergeClassStudentsApi = async (
    classId: string | number,
    payload: { source_class_id: number; mapping: { source_student_id: number; target_student_id: number | null }[] }
): Promise<{ merged: boolean; targetClassId?: string; notified?: number; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { merged: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/merge-students`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { merged: false, error: (data as any)?.error || (data as any)?.message || 'Không thể gộp lớp.' };
        return {
            merged: true,
            targetClassId: (data as any).targetClassId != null ? String((data as any).targetClassId) : undefined,
            notified: (data as any).notified,
        };
    } catch (e: any) {
        return { merged: false, error: e?.message || 'Lỗi khi gộp lớp.' };
    }
};

/**
 * Xuất bảng điểm 1 bài kiểm tra/bài tập ra CSV đúng cột SMAS/vnEdu quen
 * thuộc (STT/Họ và tên/Điểm) — Phase 5.3. Không tích hợp API 2 chiều với
 * SMAS/vnEdu (hệ thống đóng), chỉ gỡ nỗi đau nhập điểm 2 nơi bằng cách xuất
 * đúng định dạng để dán/import lại. Trả về true nếu tải thành công.
 */
export const exportClassGradesCsvApi = async (classId: string | number, assignmentId: string | number): Promise<{ ok: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { ok: false, error: 'Chỉ dùng được ở chế độ production.' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/export/grades?assignment_id=${assignmentId}`);
        if (!res.ok) return { ok: false, error: 'Không thể xuất bảng điểm.' };
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const disposition = res.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename="?([^"]+)"?/);
        a.href = url;
        a.download = match ? match[1] : 'bang-diem.csv';
        a.click();
        URL.revokeObjectURL(url);
        return { ok: true };
    } catch (e: any) {
        return { ok: false, error: e?.message || 'Lỗi khi xuất bảng điểm.' };
    }
};

// =================================================================================
// Trường học (School Organization) — Phase 5.0
// =================================================================================

/** Danh sách trường mà user đang là thành viên (kèm myRole) */
export const listMySchoolsApi = async (): Promise<SchoolSummary[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools`);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.schools) ? data.schools : [];
    } catch { return []; }
};

/** Tạo trường mới — actor tự động là admin + người sáng lập */
/** Gửi YÊU CẦU tạo trường — chưa active ngay, chờ BOOKAI duyệt (xem SchoolController::store()). */
export const createSchoolApi = async (
    payload: { name: string; level?: string; address?: string; contact_email?: string; contact_phone: string; requested_domain?: string }
): Promise<{ school: SchoolSummary } | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { error: 'Chỉ dùng được ở chế độ production.' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools`, { method: 'POST', body: JSON.stringify(payload) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể tạo trường.' };
        return { school: (data as any).school };
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi tạo trường.' };
    }
};

export const updateSchoolApi = async (
    schoolId: string | number,
    // verified_domain KHÔNG có ở đây nữa — chỉ đội ngũ BOOKAI gán được qua panel nội bộ (Phase 5.4 fix bảo mật).
    payload: Partial<{ name: string; level: string; address: string; contact_email: string; contact_phone: string; logo_url: string; default_textbook_set: string }>
): Promise<{ school: SchoolSummary } | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { error: 'Chỉ dùng được ở chế độ production.' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}`, { method: 'PATCH', body: JSON.stringify(payload) });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể cập nhật trường.' };
        return { school: (data as any).school };
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi cập nhật trường.' };
    }
};

/** Danh sách thành viên active của 1 trường (bất kỳ thành viên nào cũng xem được) */
export const listSchoolMembersApi = async (schoolId: string | number): Promise<SchoolMemberInfo[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/members`);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.members) ? data.members : [];
    } catch { return []; }
};

/** Mời 1 GV tham gia trường (chỉ role=admin của trường). Trả token link mời khi thành công. */
export const inviteSchoolMemberApi = async (
    schoolId: string | number,
    payload: { email: string; role?: 'admin' | 'teacher' }
): Promise<{ token: string; invitation?: any } | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { token: 'demo-token' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/members/invite`, {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể tạo lời mời.' };
        return { token: (data as any).token, invitation: (data as any).invitation };
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi tạo lời mời.' };
    }
};

export const updateSchoolMemberApi = async (
    schoolId: string | number,
    memberId: string | number,
    payload: { role: 'admin' | 'teacher' }
): Promise<{ updated: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { updated: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/members/${memberId}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { updated: false, error: (data as any)?.error || (data as any)?.message || 'Không thể cập nhật.' };
        return { updated: true };
    } catch (e: any) {
        return { updated: false, error: e?.message || 'Lỗi khi cập nhật.' };
    }
};

export const removeSchoolMemberApi = async (
    schoolId: string | number,
    memberId: string | number
): Promise<{ removed: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { removed: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/members/${memberId}`, { method: 'DELETE' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { removed: false, error: (data as any)?.error || (data as any)?.message || 'Không thể gỡ thành viên.' };
        return { removed: true };
    } catch (e: any) {
        return { removed: false, error: e?.message || 'Lỗi khi gỡ thành viên.' };
    }
};

/** Tự rời trường (lỗi nếu là người sáng lập hoặc admin cuối cùng) */
export const leaveSchoolApi = async (schoolId: string | number): Promise<{ left: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { left: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/leave`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { left: false, error: (data as any)?.error || (data as any)?.message || 'Không thể rời trường.' };
        return { left: true };
    } catch (e: any) {
        return { left: false, error: e?.message || 'Lỗi khi rời trường.' };
    }
};

/** Xem trước thông tin lời mời tham gia trường theo token */
export const previewSchoolInvitationApi = async (
    token: string
): Promise<{ invitation: SchoolInvitationPreview } | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') {
        return { invitation: { schoolName: 'Trường mẫu', inviterName: 'Admin mẫu', role: 'teacher', memberCount: 0, expiresAt: null } };
    }
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/school-invitations/${token}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            if (res.status === 404) return { error: 'Không tìm thấy lời mời này.' };
            if (res.status === 410) return { error: 'Lời mời đã hết hạn hoặc đã được sử dụng.' };
            return { error: (data as any)?.error || (data as any)?.message || 'Không thể tải thông tin lời mời.' };
        }
        return { invitation: (data as any).invitation };
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi tải thông tin lời mời.' };
    }
};

/** Chấp nhận lời mời tham gia trường */
export const acceptSchoolInvitationApi = async (token: string): Promise<{ joined: boolean; schoolId?: string; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { joined: true, schoolId: '1' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/school-invitations/${token}/accept`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            if (res.status === 404) return { joined: false, error: 'Không tìm thấy lời mời này.' };
            if (res.status === 410) return { joined: false, error: 'Lời mời đã hết hạn hoặc đã được sử dụng.' };
            return { joined: false, error: (data as any)?.error || (data as any)?.message || 'Không thể tham gia trường.' };
        }
        return { joined: true, schoolId: (data as any).schoolId != null ? String((data as any).schoolId) : undefined };
    } catch (e: any) {
        return { joined: false, error: e?.message || 'Lỗi khi tham gia trường.' };
    }
};

/**
 * Trường có `verified_domain` khớp đúng domain email của user hiện tại VÀ
 * user chưa là thành viên — Phase 5.4 ("SSO theo domain", rút gọn thực dụng:
 * dùng lại login Google/Facebook cá nhân đã có, không đăng ký OAuth app
 * Workspace/Microsoft riêng vì cần quyền quản trị domain của chính trường).
 */
export const getSchoolDomainMatchesApi = async (): Promise<SchoolSummary[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/domain-matches`);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.schools) ? data.schools : [];
    } catch { return []; }
};

/**
 * GV gửi YÊU CẦU tham gia trường nếu email đã xác thực (Google/Facebook)
 * khớp verified_domain — KHÔNG vào ngay, admin trường phải duyệt (Phase 5.4
 * fix bảo mật: email tự khai không đủ căn cứ để cấp quyền truy cập ngay).
 */
export const joinSchoolByDomainApi = async (schoolId: string | number): Promise<{ requested: boolean; status?: string; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { requested: true, status: 'pending' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/join-by-domain`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { requested: false, error: (data as any)?.error || (data as any)?.message || 'Không thể gửi yêu cầu.' };
        return { requested: true, status: (data as any).status };
    } catch (e: any) {
        return { requested: false, error: e?.message || 'Lỗi khi gửi yêu cầu.' };
    }
};

/** Danh sách yêu cầu tham gia qua domain đang chờ duyệt — chỉ admin trường. */
export const getSchoolDomainRequestsApi = async (schoolId: string | number): Promise<{ id: string; userId: string; name: string | null; email: string | null; requestedAt: string }[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/domain-requests`);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.requests) ? data.requests : [];
    } catch { return []; }
};

export const approveSchoolDomainRequestApi = async (schoolId: string | number, memberId: string | number): Promise<{ approved: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { approved: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/domain-requests/${memberId}/approve`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { approved: false, error: (data as any)?.error || (data as any)?.message || 'Không thể duyệt.' };
        return { approved: true };
    } catch (e: any) {
        return { approved: false, error: e?.message || 'Lỗi khi duyệt.' };
    }
};

export const rejectSchoolDomainRequestApi = async (schoolId: string | number, memberId: string | number): Promise<{ rejected: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { rejected: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/domain-requests/${memberId}/reject`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { rejected: false, error: (data as any)?.error || (data as any)?.message || 'Không thể từ chối.' };
        return { rejected: true };
    } catch (e: any) {
        return { rejected: false, error: e?.message || 'Lỗi khi từ chối.' };
    }
};

/** Từ chối lời mời tham gia trường */
export const declineSchoolInvitationApi = async (token: string): Promise<{ declined: boolean }> => {
    if (APP_MODE !== 'PRODUCTION') return { declined: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/school-invitations/${token}/decline`, { method: 'POST' });
        return { declined: res.ok };
    } catch { return { declined: false }; }
};

// =================================================================================
// School Credit Pool — Phase 5.1 (chỉ admin trường xem/thao tác)
// =================================================================================

/** Tổng quan ngân sách credit của trường: balance admin, tổng quota đã đặt, đã phân bổ chu kỳ này chưa */
export const getSchoolCreditSummaryApi = async (schoolId: string | number): Promise<SchoolCreditSummary | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { error: 'Chỉ dùng được ở chế độ production.' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/credit-pool`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể tải ngân sách credit.' };
        return data as SchoolCreditSummary;
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi tải ngân sách credit.' };
    }
};

/** Đặt hạn mức credit/tháng mong muốn cho 1 GV — CHƯA trừ tiền, chỉ là yêu cầu chờ phân bổ */
export const setSchoolCreditQuotaApi = async (
    schoolId: string | number,
    memberUserId: string | number,
    monthlyQuota: number
): Promise<{ updated: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { updated: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/credit-pool/members/${memberUserId}`, {
            method: 'PATCH',
            body: JSON.stringify({ monthly_quota: monthlyQuota }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { updated: false, error: (data as any)?.error || (data as any)?.message || 'Không thể cập nhật hạn mức.' };
        return { updated: true };
    } catch (e: any) {
        return { updated: false, error: e?.message || 'Lỗi khi cập nhật hạn mức.' };
    }
};

/** Chạy phân bổ credit cho chu kỳ (tháng) hiện tại — trừ từ balance admin, cộng vào balance từng GV theo quota */
export const allocateSchoolCreditApi = async (
    schoolId: string | number
): Promise<{ allocated: number; totalRequested: number; cycleLabel: string; reason?: string } | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { error: 'Chỉ dùng được ở chế độ production.' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/credit-pool/allocate`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể phân bổ credit.' };
        return data;
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi phân bổ credit.' };
    }
};

/**
 * Dashboard hiệu trưởng/tổ trưởng chuyên môn — Phase 5.5. Mục đích thật là
 * công cụ change-management (GV nào đã dùng/chưa dùng), không phải phân tích
 * học tập sâu — xem docs/SCHOOL_ROLLOUT_STRATEGY_2026.md mục 3.4.
 */
export const getSchoolDashboardApi = async (schoolId: string | number): Promise<SchoolDashboardSummary | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { error: 'Chỉ dùng được ở chế độ production.' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/dashboard`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể tải dashboard.' };
        return data as SchoolDashboardSummary;
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi tải dashboard.' };
    }
};

// =================================================================================
// School Bulk Import — Phase 5.2 (admin trường import CSV + luồng gắn lớp đã có)
// =================================================================================

export const bulkImportSchoolClassesApi = async (
    schoolId: string | number,
    rows: SchoolBulkImportRow[],
    createMissingAccounts: boolean = false
): Promise<{ results: SchoolBulkImportResult[] } | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { error: 'Chỉ dùng được ở chế độ production.' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/bulk-import`, {
            method: 'POST',
            body: JSON.stringify({ rows, create_missing_accounts: createMissingAccounts }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể import.' };
        return data;
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi import.' };
    }
};

/** Gợi ý gắn lớp đang chờ CHÍNH user hiện tại (với vai trò GVCN) xác nhận, cho 1 trường cụ thể */
export const getSchoolPendingClassLinksApi = async (schoolId: string | number): Promise<SchoolClassLinkSuggestion[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/bulk-import/pending-links`);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.pendingLinks) ? data.pendingLinks : [];
    } catch { return []; }
};

export const confirmSchoolClassLinkApi = async (
    schoolId: string | number,
    suggestionId: string | number
): Promise<{ confirmed: boolean; classId?: string; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { confirmed: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/bulk-import/pending-links/${suggestionId}/confirm`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { confirmed: false, error: (data as any)?.error || (data as any)?.message || 'Không thể xác nhận.' };
        return { confirmed: true, classId: (data as any).classId };
    } catch (e: any) {
        return { confirmed: false, error: e?.message || 'Lỗi khi xác nhận.' };
    }
};

export const declineSchoolClassLinkApi = async (
    schoolId: string | number,
    suggestionId: string | number
): Promise<{ declined: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { declined: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/bulk-import/pending-links/${suggestionId}/decline`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { declined: false, error: (data as any)?.error || (data as any)?.message || 'Không thể từ chối.' };
        return { declined: true };
    } catch (e: any) {
        return { declined: false, error: e?.message || 'Lỗi khi từ chối.' };
    }
};

// =================================================================================
// Tổ chuyên môn (Subject Department) — Phase 6.1-6.6
// =================================================================================

/** Danh sách tổ chuyên môn của trường — mọi thành viên trường xem được. */
export const getSchoolDepartmentsApi = async (schoolId: string | number): Promise<SchoolDepartmentSummary[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments`);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.departments) ? data.departments : [];
    } catch { return []; }
};

/** Tạo tổ mới — chỉ admin trường (giống BGH quyết định cơ cấu tổ). */
export const createSchoolDepartmentApi = async (
    schoolId: string | number,
    name: string,
    subjectLabel?: string,
    headEmail?: string
): Promise<{ department?: SchoolDepartmentSummary; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { error: 'Chỉ dùng được ở chế độ production.' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments`, {
            method: 'POST',
            body: JSON.stringify({ name, subject_label: subjectLabel || undefined, head_email: headEmail || undefined }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể tạo tổ.' };
        return { department: (data as any).department };
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi tạo tổ.' };
    }
};

export const updateSchoolDepartmentApi = async (
    schoolId: string | number,
    departmentId: string | number,
    updates: { name?: string; subjectLabel?: string }
): Promise<{ department?: SchoolDepartmentSummary; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { error: 'Chỉ dùng được ở chế độ production.' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}`, {
            method: 'PATCH',
            body: JSON.stringify({ name: updates.name, subject_label: updates.subjectLabel }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể cập nhật tổ.' };
        return { department: (data as any).department };
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi cập nhật tổ.' };
    }
};

/** Xoá tổ — chỉ admin trường. */
export const deleteSchoolDepartmentApi = async (
    schoolId: string | number,
    departmentId: string | number
): Promise<{ deleted: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { deleted: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}`, { method: 'DELETE' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { deleted: false, error: (data as any)?.error || (data as any)?.message || 'Không thể xoá tổ.' };
        return { deleted: true };
    } catch (e: any) {
        return { deleted: false, error: e?.message || 'Lỗi khi xoá tổ.' };
    }
};

export const getSchoolDepartmentMembersApi = async (
    schoolId: string | number,
    departmentId: string | number
): Promise<SchoolDepartmentMemberInfo[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/members`);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.members) ? data.members : [];
    } catch { return []; }
};

/** Gợi ý GV trong trường phù hợp tổ (theo subject_label) — chỉ tổ trưởng/admin trường. */
export const getSchoolDepartmentSuggestionsApi = async (
    schoolId: string | number,
    departmentId: string | number
): Promise<SchoolDepartmentSuggestion[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/suggest-members`);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.suggestions) ? data.suggestions : [];
    } catch { return []; }
};

/** Thêm trực tiếp 1 GV (đã là thành viên trường) vào tổ theo email — tổ trưởng/admin trường. */
export const addSchoolDepartmentMemberApi = async (
    schoolId: string | number,
    departmentId: string | number,
    email: string,
    role?: 'head' | 'member'
): Promise<{ added: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { added: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/members`, {
            method: 'POST',
            body: JSON.stringify({ email, role }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { added: false, error: (data as any)?.error || (data as any)?.message || 'Không thể thêm thành viên.' };
        return { added: true };
    } catch (e: any) {
        return { added: false, error: e?.message || 'Lỗi khi thêm thành viên.' };
    }
};

export const updateSchoolDepartmentMemberApi = async (
    schoolId: string | number,
    departmentId: string | number,
    memberId: string | number,
    role: 'head' | 'member'
): Promise<{ updated: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { updated: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/members/${memberId}`, {
            method: 'PATCH',
            body: JSON.stringify({ role }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { updated: false, error: (data as any)?.error || (data as any)?.message || 'Không thể cập nhật vai trò.' };
        return { updated: true };
    } catch (e: any) {
        return { updated: false, error: e?.message || 'Lỗi khi cập nhật vai trò.' };
    }
};

export const removeSchoolDepartmentMemberApi = async (
    schoolId: string | number,
    departmentId: string | number,
    memberId: string | number
): Promise<{ removed: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { removed: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/members/${memberId}`, { method: 'DELETE' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { removed: false, error: (data as any)?.error || (data as any)?.message || 'Không thể gỡ thành viên.' };
        return { removed: true };
    } catch (e: any) {
        return { removed: false, error: e?.message || 'Lỗi khi gỡ thành viên.' };
    }
};

export const leaveSchoolDepartmentApi = async (
    schoolId: string | number,
    departmentId: string | number
): Promise<{ left: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { left: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/leave`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { left: false, error: (data as any)?.error || (data as any)?.message || 'Không thể rời tổ.' };
        return { left: true };
    } catch (e: any) {
        return { left: false, error: e?.message || 'Lỗi khi rời tổ.' };
    }
};

/** GV tự xin vào tổ — cần tổ trưởng duyệt (mirror luồng xin vào trường qua domain). */
export const requestJoinSchoolDepartmentApi = async (
    schoolId: string | number,
    departmentId: string | number
): Promise<{ requested: boolean; status?: string; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { requested: true, status: 'pending' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/join-request`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { requested: false, error: (data as any)?.error || (data as any)?.message || 'Không thể gửi yêu cầu.' };
        return { requested: true, status: (data as any).status };
    } catch (e: any) {
        return { requested: false, error: e?.message || 'Lỗi khi gửi yêu cầu.' };
    }
};

export const getSchoolDepartmentJoinRequestsApi = async (
    schoolId: string | number,
    departmentId: string | number
): Promise<SchoolDepartmentJoinRequest[]> => {
    if (APP_MODE !== 'PRODUCTION') return [];
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/join-requests`);
        if (!res.ok) return [];
        const data = await res.json().catch(() => ({}));
        return Array.isArray(data?.requests) ? data.requests : [];
    } catch { return []; }
};

export const approveSchoolDepartmentJoinRequestApi = async (
    schoolId: string | number,
    departmentId: string | number,
    memberId: string | number
): Promise<{ approved: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { approved: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/join-requests/${memberId}/approve`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { approved: false, error: (data as any)?.error || (data as any)?.message || 'Không thể duyệt.' };
        return { approved: true };
    } catch (e: any) {
        return { approved: false, error: e?.message || 'Lỗi khi duyệt.' };
    }
};

export const rejectSchoolDepartmentJoinRequestApi = async (
    schoolId: string | number,
    departmentId: string | number,
    memberId: string | number
): Promise<{ rejected: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { rejected: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/join-requests/${memberId}/reject`, { method: 'POST' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { rejected: false, error: (data as any)?.error || (data as any)?.message || 'Không thể từ chối.' };
        return { rejected: true };
    } catch (e: any) {
        return { rejected: false, error: e?.message || 'Lỗi khi từ chối.' };
    }
};

/** Báo cáo tổng hợp tổ (hồ sơ + kết quả học tập + hoạt động) — chỉ tổ trưởng/admin trường, số liệu nhạy cảm. */
export const getSchoolDepartmentReportApi = async (
    schoolId: string | number,
    departmentId: string | number
): Promise<SchoolDepartmentReport | { error: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { error: 'Chỉ dùng được ở chế độ production.' };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/schools/${schoolId}/departments/${departmentId}/report`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { error: (data as any)?.error || (data as any)?.message || 'Không thể tải báo cáo.' };
        return data as SchoolDepartmentReport;
    } catch (e: any) {
        return { error: e?.message || 'Lỗi khi tải báo cáo.' };
    }
};

/**
 * Tải tệp PDF từ Backend để đảm bảo chất lượng và font Tiếng Việt
 */
export const downloadPdfFromBackend = async (type: 'lesson-plan' | 'quiz' | 'homework' | 'activity', id: string | number, filename: string) => {
    if (id.toString().startsWith('id_')) {
        throw new Error('Vui lòng lưu tài liệu trước khi tải PDF.');
    }
    const path = `${API_EDU_PREFIX}/export/pdf/${type}/${id}?_t=${Date.now()}`;
    /** UI thường fallback sang PDF phía trình duyệt — không log error ồn ào khi chỉ cần thử máy chủ trước */
    return downloadFromApi(path, filename, { silent: true });
};

/**
 * DOCX từ backend: LaTeX → KaTeX HTML → DOCX (hiện hỗ trợ quiz).
 */
export const downloadDocxFromBackend = async (
    type: 'quiz',
    id: string | number,
    filename: string,
    options?: { variant?: 'student' | 'answers' | 'full' },
) => {
    if (id.toString().startsWith('id_')) {
        throw new Error('Vui lòng lưu tài liệu trước khi tải DOCX.');
    }
    const variant = options?.variant ?? 'student';
    const path = `${API_EDU_PREFIX}/export/docx/${type}/${id}?variant=${encodeURIComponent(variant)}`;
    const name = filename.endsWith('.docx') ? filename : `${filename}.docx`;
    return downloadFromApi(path, name, { silent: true });
};

/**
 * Tải tệp từ API (hỗ trợ xác thực)
 */
export const downloadFromApi = async (
    path: string,
    filename: string,
    options?: { silent?: boolean }
) => {
    try {
        const res = await apiFetch(path);
        if (!res.ok) {
            let detail = `HTTP ${res.status}`;
            const ct = res.headers.get('content-type') || '';
            if (ct.includes('application/json')) {
                const j = await res.json().catch(() => ({}));
                if (j && typeof j === 'object' && (j as any).error) detail = String((j as any).error);
            }
            throw new Error(detail === `HTTP ${res.status}` ? 'Không thể tải tệp' : `Không thể tải tệp: ${detail}`);
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (error) {
        if (!options?.silent) console.error('Download error:', error);
        throw error;
    }
};

/**
 * Ensures a minimum delay for an async operation, useful for showing ads
 * on loading screens for at least a certain amount of time.
 */
export const withMinimumDelay = async <T>(promise: Promise<T>, minDelayMs: number = 6000, shouldDelay: boolean = true): Promise<T> => {
    if (!shouldDelay) return await promise;
    const start = Date.now();
    try {
        const result = await promise;
        const elapsed = Date.now() - start;
        if (elapsed < minDelayMs) {
            await new Promise(resolve => setTimeout(resolve, minDelayMs - elapsed));
        }
        return result;
    } catch (error) {
        const elapsed = Date.now() - start;
        if (elapsed < minDelayMs) {
            await new Promise(resolve => setTimeout(resolve, minDelayMs - elapsed));
        }
        throw error;
    }
};

/**
 * Handles errors from the Gemini API and returns a user-friendly error object.
 */
const handleGeminiError = (error: unknown, context: string): Error => {
    console.error(`Lỗi trong khi ${context}:`, error);

    let userFriendlyMessage = `Đã xảy ra lỗi không xác định trong khi ${context}. Vui lòng thử lại.`;
    let errorMessage = '';

    if (error instanceof Error) {
        errorMessage = error.message;
    } else if (typeof error === 'string') {
        errorMessage = error;
    } else {
        try {
            // Attempt to stringify if it's an object, which might be the case for API error responses
            errorMessage = JSON.stringify(error);
        } catch {
            errorMessage = 'Lỗi không thể chuyển thành chuỗi.';
        }
    }
    
    // Convert to lower case for easier matching
    const lowerErrorMessage = errorMessage.toLowerCase();

    // Check for common Gemini API error patterns
    if (lowerErrorMessage.includes('hết lượt') || lowerErrorMessage.includes('credits') || lowerErrorMessage.includes('insufficient credit') || lowerErrorMessage.includes('payment required')) {
        userFriendlyMessage = 'Không đủ tín dụng để thực hiện tính năng này. Vui lòng mua thêm gói tín dụng.';
        (error as any).isRetryable = false;
        (error as any).errorType = 'INSUFFICIENT_CREDITS';
    } else if (lowerErrorMessage.includes('resource_exhausted') || lowerErrorMessage.includes('429')) {
        userFriendlyMessage = 'Bạn đã vượt quá hạn ngạch sử dụng API cho tính năng này. Vui lòng kiểm tra gói cước của bạn hoặc thử lại sau.';
        (error as any).isRetryable = true;
        (error as any).errorType = 'RATE_LIMIT';
    } else if (lowerErrorMessage.includes('api key not valid') || (lowerErrorMessage.includes('[400') && lowerErrorMessage.includes('api_key'))) {
        userFriendlyMessage = 'Khóa API không hợp lệ. Vui lòng kiểm tra lại cấu hình và đảm bảo khóa API của bạn đúng.';
        (error as any).isRetryable = false;
        (error as any).errorType = 'CONFIG_ERROR';
    } else if (lowerErrorMessage.includes('safety') || lowerErrorMessage.includes('blocked')) {
        userFriendlyMessage = 'Yêu cầu của bạn đã bị chặn vì lý do an toàn (chứa nội dung không phù hợp). Vui lòng điều chỉnh lại kịch bản.';
        (error as any).isRetryable = false;
        (error as any).errorType = 'SAFETY';
    } else if (lowerErrorMessage.includes('failed to parse json') || lowerErrorMessage.includes('invalid json')) {
        userFriendlyMessage = 'AI đã trả về dữ liệu có định dạng không hợp lệ. Vui lòng thử lại.';
        (error as any).isRetryable = true;
        (error as any).errorType = 'INVALID_RESPONSE';
    } else if (lowerErrorMessage.includes('deadline exceeded') || lowerErrorMessage.includes('504') || lowerErrorMessage.includes('timeout') || lowerErrorMessage.includes('failed to fetch')) {
        userFriendlyMessage = 'Yêu cầu đã hết thời gian chờ hoặc lỗi kết nối. Máy chủ có thể đang bận. Vui lòng thử lại.';
        (error as any).isRetryable = true;
        (error as any).errorType = 'TIMEOUT';
    } else if (lowerErrorMessage.includes('[500')) {
        userFriendlyMessage = 'Đã có lỗi xảy ra từ phía máy chủ AI. Vui lòng thử lại sau.';
        (error as any).isRetryable = true;
        (error as any).errorType = 'SERVER_ERROR';
    }

    const finalError = new Error(userFriendlyMessage);
    (finalError as any).isRetryable = (error as any).isRetryable ?? false;
    (finalError as any).errorType = (error as any).errorType ?? 'UNKNOWN';
    return finalError;
};


// --- API Functions ---

/**
 * Kết quả phân mảnh tài liệu.
 *  - `segments`: danh sách phân đoạn đã chia.
 *  - `source`: nguồn dữ liệu cuối cùng được dùng để segment.
 *      - `cached`: doc đã có segments lưu sẵn (free, không gọi gì).
 *      - `markdown`: backend tách bằng MarkdownSegmenter local (free).
 *      - `ai`: backend phải gọi AI provider (có trừ credit).
 *  - `cost`: số credit đã trừ (0 với `cached`/`markdown`).
 */
export interface SegmentDocumentResult {
    segments: DocumentSegment[];
    source: 'cached' | 'markdown' | 'ai';
    cost: number;
}

/**
 * Phân mảnh tài liệu — markdown-first, đồng nhất với Smart Hybrid backend.
 *
 * Cách gọi linh hoạt:
 *  - Khi có `documentId`: chỉ cần truyền id, backend tự lấy `content_markdown`
 *    (hoặc `content`) của doc. KHÔNG cần truyền `content` từ phía client.
 *  - Khi không có `documentId` (ad-hoc, vd dán nội dung tay): truyền cả
 *    `content` và `documentName`.
 *  - `forceAi`: bỏ qua cache + markdown segmenter, ép gọi AI (trừ credit).
 *
 * Phía gọi nên kiểm tra `result.source` / `result.cost` để biết có nên hỏi
 * người dùng xác nhận trừ credit hay không (chỉ cần hỏi khi `source === 'ai'`).
 */
export const segmentDocument = async (
    content: string | null | undefined,
    documentName: string | null | undefined,
    documentId?: string,
    options?: { forceAi?: boolean }
): Promise<SegmentDocumentResult> => {
    const payload: Record<string, any> = {};
    if (typeof content === 'string' && content.length > 0) payload.content = content;
    if (typeof documentName === 'string' && documentName.length > 0) payload.documentName = documentName;
    if (documentId) payload.documentId = documentId;
    if (options?.forceAi) payload.force_ai = true;

    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/segments`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Lỗi từ máy chủ khi phân tích tài liệu.');
        }
        const data = await response.json();
        const source: SegmentDocumentResult['source'] = (data.source === 'cached' || data.source === 'markdown') ? data.source : 'ai';
        const cost = typeof data.cost === 'number' ? data.cost : 0;
        // Chỉ refresh credit balance khi backend báo thực sự có trừ credit.
        if (source === 'ai' && cost > 0) {
            await refreshCreditBalance('segments');
        }
        return {
            segments: Array.isArray(data.segments) ? data.segments : [],
            source,
            cost,
        };
    } catch (error) {
        throw handleGeminiError(error, "phân tích tài liệu từ máy chủ");
    }
};

export const generateLessonPlan = async (
    gradeLevel: GradeLevel,
    subject: string,
    topic: string,
    detailedInstruction: string,
    contextContent: string | null,
    model: TeachingModel,
    teachingDuration: number,
    allowAiAugmentation: boolean,
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraOptions?: {
        classLevel?: string;
        textbookSet?: string;
        selectedDigitalCompetencies?: string[];
        sourceDocumentIds?: string[];
        selectedSourceIds?: string[];
        sourceMode?: 'ai' | 'document' | 'image';
        sourceImageIds?: (number | string)[];
        sourceTextbookMeta?: any;
        /** Sơ đồ tư duy làm nguyên liệu — backend bơm outline vào contextContent */
        mindmapId?: string | number;
    }
): Promise<LessonPlan> => {
    const payload = { gradeLevel, subject, topic, detailedInstruction, contextContent, model, teachingDuration, allowAiAugmentation, aiProvider, id, ...extraOptions };
    if ((payload as any).sourceDocumentIds && !(payload as any).source_document_ids) {
        (payload as any).source_document_ids = (payload as any).sourceDocumentIds;
    }
    if ((payload as any).sourceImageIds && !(payload as any).source_image_ids) {
        (payload as any).source_image_ids = (payload as any).sourceImageIds;
    }
    if ((payload as any).sourceTextbookMeta && !(payload as any).source_textbook_meta) {
        (payload as any).source_textbook_meta = typeof (payload as any).sourceTextbookMeta === 'string'
            ? (payload as any).sourceTextbookMeta
            : JSON.stringify((payload as any).sourceTextbookMeta);
    }
    if ((payload as any).sourceMode && !(payload as any).source_mode) {
        (payload as any).source_mode = (payload as any).sourceMode;
    }
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/lesson-plan`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Lỗi từ máy chủ khi tạo giáo án.');
        }
        const result = await response.json();
        await refreshCreditBalance('lesson-plan');
        return { ...result, gradeLevel, model, teachingDuration, ...extraOptions };
    } catch (error) {
        throw handleGeminiError(error, "tạo giáo án từ máy chủ");
    }
};

export interface GeneratedImageResult {
    imageUrl: string;
    imageId?: string | number;
    image?: any;
}

/**
 * Tham chiếu resource cha (giáo án, bài tập...) khi tạo ảnh từ trong trang đó.
 * Backend lưu vào meta giao dịch credit để TransactionHistory hiển thị "thuộc bài X"
 * và cho phép click sang xem chính bài đó.
 */
export interface ImageGenerationContext {
    resource_type: 'lesson_plan' | 'slides' | 'presentation' | 'homework' | 'quiz' | 'game' | 'activity' | 'oral_quiz';
    resource_id: string | number;
    resource_title?: string;
}

export const generateImage = async (prompt: string, context?: ImageGenerationContext): Promise<GeneratedImageResult> => {
    // Luôn gọi xuống backend để tạo hình ảnh, lưu trữ và quản lý credit
    const payload: { prompt: string; context?: ImageGenerationContext } = { prompt };
    if (context && context.resource_type && (context.resource_id || context.resource_id === 0)) {
        payload.context = context;
    }
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/image`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Lỗi từ máy chủ khi tạo hình ảnh.');
        }
        const data = await response.json();
        await refreshCreditBalance('image');
        return {
            imageUrl: data.imageUrl,
            imageId: data.imageId ?? data.image?.id,
            image: data.image,
        };
    } catch (error) {
        throw handleGeminiError(error, "tạo hình ảnh từ máy chủ");
    }
};

export const generateImageForSlide = async (
    slide: Slide,
    presentationTheme?: Presentation['theme'],
    presentationRef?: { id?: string | number; title?: string }
): Promise<{ newPrompt?: string, imageUrl: string, imageId?: string | number }> => {
    const themeStyle = presentationThemeToPromptLabel(presentationTheme);
    
    // FIX: The original prompt was too long, causing a token limit error.
    // This now uses the dedicated `imagePrompt` from the slide content, which is concise and designed for image generation.
    // A simple fallback is created if `imagePrompt` is missing.
    const c = slide.content;
    const slideContent = c.explanation || c.activityDescription || c.question || c.discussionQuestion || 
                        (c.mainPoints ? c.mainPoints.join(', ') : '') || 
                        (c.summaryPoints ? c.summaryPoints.join(', ') : '');
    const finalPrompt = c.imagePrompt || `Một bức hình minh họa kỹ thuật số sạch sẽ, hiện đại, mang tính giáo dục theo chủ đề "${themeStyle}" về "${slide.title}". ${slideContent ? `Ngữ cảnh: ${slideContent}` : ''}.`;
    
    // Safeguard against excessively long prompts
    if (finalPrompt.length > 4000) {
        throw new Error("Image prompt exceeds the safe length limit.");
    }
    
    const ctx: ImageGenerationContext | undefined = presentationRef?.id && !String(presentationRef.id).startsWith('id_')
        ? { resource_type: 'slides', resource_id: presentationRef.id, resource_title: presentationRef.title }
        : undefined;
    const generated = await generateImage(finalPrompt, ctx);
    return { imageUrl: generated.imageUrl, imageId: generated.imageId };
};

export const generateSlides = async (
    topic: string,
    detailedInstruction: string,
    /** Chuỗi tự do khi phụ huynh chọn «không gắn cấp học» — khớp prompt backend. */
    educationalLevel: GradeLevel | string,
    contextContent: string | null,
    generationOptions: { mode: 'auto', duration: number } | { mode: 'custom', count: number },
    includeIntro: boolean,
    includeSummary: boolean,
    allowAiAugmentation: boolean,
    sourceLessonPlan?: LessonPlan,
    aiProvider: AIProvider = 'gemini',
    id?: string | number, // Thêm id để hỗ trợ cập nhật khi tạo lại
    designMode: 'basic' | 'professional' = 'professional',
    /** Gợi ý vai trò người tạo + mục đích (phụ huynh / học sinh) — backend đưa vào prompt. */
    audienceContext?: string | null,
    extraOptions?: { classLevel?: string; textbookSet?: string; sourceDocumentId?: string | number }
): Promise<Presentation> => {
    const payload = {
        topic, detailedInstruction, educationalLevel, contextContent, generationOptions, includeIntro, includeSummary, allowAiAugmentation, sourceLessonPlan, aiProvider, id, designMode,
        ...(audienceContext != null && String(audienceContext).trim() !== '' ? { audienceContext: String(audienceContext).trim() } : {}),
        ...extraOptions
    };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/slides`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Lỗi từ máy chủ khi tạo trình chiếu.');
        }
        const data = await response.json();
        await refreshCreditBalance('slides');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo trình chiếu từ máy chủ");
    }
};

/** Nâng cấp trình chiếu lên HTML5 chuyên nghiệp */
export const upgradePresentationToHtml5 = async (
    id: string | number,
    force: boolean = false,
    aiProvider: AIProvider = 'gemini'
): Promise<{ html_url: string, share_token?: string, public_html5_url?: string | null }> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/slides/upgrade-html5`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({ id, force, aiProvider })
        });
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Lỗi từ máy chủ khi nâng cấp trình chiếu.');
        }
        const data = await response.json();
        await refreshCreditBalance('slides');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "nâng cấp trình chiếu HTML5 từ máy chủ");
    }
};

/** Xuất trình chiếu chuyên nghiệp thành file PPTX qua backend */
export const exportSlidesToPptx = async (presentation: Presentation): Promise<Blob> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/slides/export-pptx`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({ presentation })
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi xuất PPTX.');
        return await response.blob();
    } catch (error) {
        throw handleGeminiError(error, "xuất file PowerPoint từ máy chủ");
    }
};

export const generateHomework = async (
    topic: string,
    detailedInstruction: string,
    contextContent: string | null,
    allowAiAugmentation: boolean,
    aiProvider: AIProvider = 'gemini',
    config?: {
        format?: string;
        mcqCount?: number;
        essayCount?: number;
        difficultyLevel?: string;
        matrixConfig?: any;
    },
    id?: string | number,
    extraOptions?: { gradeLevel?: GradeLevel; subject?: string; classLevel?: string; textbookSet?: string; sourceDocumentIds?: string[]; selectedSourceIds?: string[] }
): Promise<Homework> => {
    const payload = { topic, detailedInstruction, contextContent, allowAiAugmentation, aiProvider, ...config, id, ...extraOptions };
    if ((payload as any).sourceDocumentIds && !(payload as any).source_document_ids) {
        (payload as any).source_document_ids = (payload as any).sourceDocumentIds;
    }
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/homework`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo bài tập.');
        const data = await response.json();
        await refreshCreditBalance('homework');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo bài tập từ máy chủ");
    }
};

/**
 * Phân tích ĐA ẢNH tài liệu / nội dung bài học trong thư viện để tổng hợp tài liệu nguồn cho các module tạo nội dung.
 *
 * Backend giới hạn tối đa 10 ảnh, mỗi ảnh ≤ 5MB, tổng ≤ 25MB và cache kết quả 24h theo
 * tổ hợp (ID ảnh + chế độ ghi chép) để không tốn token khi mở lại cùng tham số.
 *
 * Tham số `verbatim` (mặc định true):
 *  - true  → "Giữ nguyên gốc": AI chép lại nguyên văn nội dung trong ảnh (chính xác cao,
 *            bám sát tài liệu gốc, nhiều token hơn).
 *  - false → "Diễn đạt lại": AI viết lại theo phong cách sư phạm (an toàn bản quyền hơn,
 *            cô đọng, ít token hơn).
 */
export const analyzeTextbookImagesFromLibrary = async (
    imageIds: Array<string | number>,
    options?: { forceRefresh?: boolean; verbatim?: boolean }
): Promise<{
    topic: string;
    subject_hint: string;
    grade_hint: string;
    textbook_set_hint: string;
    language_detected: string;
    summary: string;
    key_concepts: { term: string; explanation: string }[];
    vocabulary: { term: string; meaning: string }[];
    formulas: { name: string; expression: string; note?: string }[];
    exercises_seen: string[];
    lesson_objectives: string[];
    per_page: {
        page_index: number;
        image_id: number | string;
        image_name?: string;
        page_label?: string;
        transcription: string;
        key_points: string[];
        ocr_confidence?: 'high' | 'medium' | 'low';
    }[];
    image_ids: (number | string)[];
    verbatim_mode: boolean;
    cached: boolean;
}> => {
    const normalizedIds = (Array.isArray(imageIds) ? imageIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    if (normalizedIds.length === 0) {
        throw new Error('Cần ít nhất 1 ảnh để phân tích.');
    }
    if (normalizedIds.length > 10) {
        throw new Error('Chỉ phân tích tối đa 10 ảnh mỗi lượt.');
    }

    const verbatim = options?.verbatim !== false; // default true (giữ nguyên gốc)

    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/textbook-images-meta`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({
                image_ids: normalizedIds,
                force_refresh: !!options?.forceRefresh,
                verbatim_mode: verbatim,
            }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || 'Không thể phân tích tập ảnh tài liệu.');
        }
        const json = await response.json();
        const d = json.data || json;
        const perPage = Array.isArray(d.per_page) ? d.per_page : [];
        return {
            topic: String(d.topic || ''),
            subject_hint: String(d.subject_hint || ''),
            grade_hint: String(d.grade_hint || ''),
            textbook_set_hint: String(d.textbook_set_hint || ''),
            language_detected: String(d.language_detected || ''),
            summary: String(d.summary || ''),
            key_concepts: Array.isArray(d.key_concepts)
                ? d.key_concepts.map((kc: any) => ({
                    term: String(kc?.term || ''),
                    explanation: String(kc?.explanation || ''),
                }))
                : [],
            vocabulary: Array.isArray(d.vocabulary)
                ? d.vocabulary.map((v: any) => ({
                    term: String(v?.term || ''),
                    meaning: String(v?.meaning || ''),
                }))
                : [],
            formulas: Array.isArray(d.formulas)
                ? d.formulas.map((f: any) => ({
                    name: String(f?.name || ''),
                    expression: String(f?.expression || ''),
                    note: f?.note != null ? String(f.note) : undefined,
                }))
                : [],
            exercises_seen: Array.isArray(d.exercises_seen) ? d.exercises_seen.map((s: any) => String(s)) : [],
            lesson_objectives: Array.isArray(d.lesson_objectives) ? d.lesson_objectives.map((s: any) => String(s)) : [],
            per_page: perPage.map((p: any, idx: number) => ({
                page_index: Number.isFinite(p?.page_index) ? Number(p.page_index) : idx,
                image_id: p?.image_id ?? p?.imageId ?? '',
                image_name: p?.image_name ?? p?.imageName ?? '',
                page_label: p?.page_label ?? p?.pageLabel ?? '',
                transcription: String(p?.transcription || ''),
                key_points: Array.isArray(p?.key_points)
                    ? p.key_points.map((s: any) => String(s))
                    : Array.isArray(p?.keyPoints)
                        ? p.keyPoints.map((s: any) => String(s))
                        : [],
                ocr_confidence: ['high', 'medium', 'low'].includes(p?.ocr_confidence) ? p.ocr_confidence : 'medium',
            })),
            image_ids: Array.isArray(d.image_ids) ? d.image_ids : normalizedIds,
            verbatim_mode: typeof d.verbatim_mode === 'boolean' ? d.verbatim_mode : verbatim,
            cached: !!json.cached,
        };
    } catch (error) {
        throw handleGeminiError(error, 'phân tích tập ảnh tài liệu');
    }
};

/**
 * Phân tích lại MỘT ảnh tài liệu đơn lẻ (gọn + rẻ hơn batch toàn bộ).
 *
 * Dùng cho UX "Phân tích lại trang này" trong panel xem lại — chỉ cập nhật
 * transcription / key_points / page_label / ocr_confidence của trang đó,
 * KHÔNG đụng đến summary / key_concepts / objectives của giáo án.
 */
export const analyzeSingleTextbookImagePage = async (
    imageId: string | number,
    options?: { forceRefresh?: boolean; verbatim?: boolean }
): Promise<{
    image_id: number | string;
    image_name?: string;
    page_label: string;
    transcription: string;
    key_points: string[];
    ocr_confidence: 'high' | 'medium' | 'low';
    verbatim_mode: boolean;
    cached: boolean;
}> => {
    const id = Number(imageId);
    if (!Number.isFinite(id) || id <= 0) {
        throw new Error('image_id không hợp lệ.');
    }
    const verbatim = options?.verbatim !== false;

    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/textbook-image-page-meta`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({
                image_id: id,
                force_refresh: !!options?.forceRefresh,
                verbatim_mode: verbatim,
            }),
        });
        const json = await response.json();
        const d = (json && typeof json === 'object' && json.data && typeof json.data === 'object') ? json.data : json;

        return {
            image_id: d.image_id ?? id,
            image_name: d.image_name != null ? String(d.image_name) : undefined,
            page_label: String(d.page_label || ''),
            transcription: String(d.transcription || ''),
            key_points: Array.isArray(d.key_points)
                ? d.key_points.map((s: any) => String(s))
                : Array.isArray(d.keyPoints)
                    ? d.keyPoints.map((s: any) => String(s))
                    : [],
            ocr_confidence: ['high', 'medium', 'low'].includes(d?.ocr_confidence) ? d.ocr_confidence : 'medium',
            verbatim_mode: typeof d.verbatim_mode === 'boolean' ? d.verbatim_mode : verbatim,
            cached: !!json.cached,
        };
    } catch (error) {
        throw handleGeminiError(error, 'phân tích lại trang ảnh');
    }
};

/**
 * Lưu hoặc cập nhật kết quả phân tích đa ảnh tài liệu (TextbookImagesMeta) thành Document.
 *
 * - Nếu `documentId` KHÔNG truyền → tạo Document mới (POST, 201).
 * - Nếu `documentId` TRUYỀN → cập nhật Document đã có (POST, 200).
 *
 * Backend tự render Markdown từ meta. FE chỉ cần gửi tên + image_ids + meta.
 */
export const saveTextbookMetaAsDocument = async (
    payload: {
        name: string;
        description?: string;
        imageIds: Array<string | number>;
        textbookMeta: any;
        verbatimMode?: boolean;
        /** Nếu truyền → cập nhật Document hiện có thay vì tạo mới. */
        documentId?: string | number | null;
    }
): Promise<{
    id: number | string;
    name: string;
    type: string;
    status: string;
    upload_date: string;
    content_markdown?: string;
    content_meta?: any;
}> => {
    const name = (payload.name || '').trim();
    if (!name) throw new Error('Tên tài liệu không được để trống.');
    const ids = (payload.imageIds || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    if (ids.length === 0) throw new Error('Cần ít nhất 1 ảnh nguồn.');
    if (!payload.textbookMeta || typeof payload.textbookMeta !== 'object') {
        throw new Error('Dữ liệu phân tích không hợp lệ.');
    }

    const docId = payload.documentId != null ? Number(payload.documentId) : null;

    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/textbook-meta-save-as-document`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({
                name,
                description: payload.description || '',
                image_ids: ids,
                textbook_meta: payload.textbookMeta,
                verbatim_mode: payload.verbatimMode !== false,
                ...(docId ? { document_id: docId } : {}),
            }),
        });
        return await response.json();
    } catch (error) {
        throw handleGeminiError(error, 'lưu tài liệu phân tích');
    }
};

/** Phân tích ảnh đề bài / trang bài tập trong thư viện để gợi ý chủ đề, format và bối cảnh (vision). */
export const analyzeHomeworkImageFromLibrary = async (
    imageId: string | number
): Promise<{
    topic: string;
    detailed_description: string;
    /** Dàn ý từng phần/câu trong ảnh (đánh số) */
    item_by_item_outline: string;
    /** Câu hỏi mẫu diễn đạt lại (mỗi dòng một stem) */
    paraphrased_stems: string;
    layout_summary: string;
    suggested_homework_format: string;
    language_detected: string;
    generation_instructions: string;
}> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/homework-image-meta`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({ image_id: imageId }),
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.message || 'Không thể phân tích ảnh.');
        }
        const json = await response.json();
        const d = json.data || json;
        return {
            topic: String(d.topic || ''),
            detailed_description: String(d.detailed_description || ''),
            item_by_item_outline: String(d.item_by_item_outline || ''),
            paraphrased_stems: String(d.paraphrased_stems || ''),
            layout_summary: String(d.layout_summary || ''),
            suggested_homework_format: String(d.suggested_homework_format || 'auto'),
            language_detected: String(d.language_detected || ''),
            generation_instructions: String(d.generation_instructions || ''),
        };
    } catch (error) {
        throw handleGeminiError(error, 'phân tích ảnh bài tập');
    }
};

export const generateActivity = async (
  gradeLevel: GradeLevel,
  activityType: ActivityType,
  objective: string,
  keywords: string,
  contextContent: string | null,
  allowAiAugmentation: boolean,
  aiProvider: AIProvider = 'gemini',
  id?: string | number,
  extraOptions?: { subject?: string; classLevel?: string; textbookSet?: string; sourceDocumentId?: string | number }
): Promise<Activity> => {
    const payload = { gradeLevel, activityType, objective, keywords, contextContent, allowAiAugmentation, aiProvider, id, ...extraOptions };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/activity`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo hoạt động.');
        const data = await response.json();
        await refreshCreditBalance('activity');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo hoạt động từ máy chủ");
    }
};

/**
 * AI tạo nội dung SKKN theo từng module.
 * Backend: POST /api/edu/generate/skkn
 * @param module - 'part1'|'part2'|'part3'|'part4'|'part5'|'part6'|'part7'
 * @param context - Dữ liệu thu thập + SKKN hiện tại
 * @param qualityLevel - 'school'|'district'|'province'
 */
export const generateSKKNSection = async (
    module: string,
    context: Record<string, any>,
    qualityLevel: 'school' | 'district' | 'province' = 'school'
): Promise<Record<string, any>> => {
    const payload = { module, context, qualityLevel };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/skkn`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.message || 'Lỗi từ máy chủ khi tạo SKKN.');
        }
        const data = await response.json();
        await refreshCreditBalance('skkn');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo nội dung SKKN từ máy chủ");
    }
};

export interface MusicGenerationOptions {
    lyrics: string;
    title?: string;
    mode?: 'quick' | 'enterprise';
    duration?: number; // seconds, up to 180 for Pro
    vocal_style?: 'realistic' | 'instrumental' | 'expressive';
    tempo?: number; // BPM
    language?: string;
    genre?: string;
    image_reference?: string; // base64 or URL
    lyrics_alignment?: boolean;
}

export const generateMusic = async (options: string | MusicGenerationOptions, title?: string, mode: 'quick' | 'enterprise' = 'quick'): Promise<any> => {
    let payload: any;
    if (typeof options === 'string') {
        payload = { lyrics: options, title, mode };
    } else {
        payload = { ...options };
        if (!payload.mode) payload.mode = mode;
    }
    
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/music`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Lỗi từ máy chủ khi tạo nhạc.');
        }
        const data = await response.json();
        await refreshCreditBalance('music');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo nhạc từ máy chủ");
    }
};

export const fetchMusicStatus = async (generationId: string): Promise<any> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/music-status?generation_id=${encodeURIComponent(generationId)}`, {
            method: 'GET',
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Lỗi từ máy chủ khi kiểm tra trạng thái tạo nhạc.');
        }
        return await response.json();
    } catch (error) {
        throw handleGeminiError(error, "kiểm tra trạng thái tạo nhạc từ máy chủ");
    }
};

export const generateStudyAssistant = async (
    messages: { role: 'user' | 'ai', text: string }[],
    opts?: {
        /** Persona backend: student (mặc định) | teacher | parent. */
        role?: 'student' | 'teacher' | 'parent';
        /** Ngữ cảnh trang đang mở — backend đưa vào system prompt. */
        context?: string;
    },
): Promise<{ reply: string; soft_notice?: string }> => {
    try {
        const path = EDUAPP_AUTH_TYPE === 'student_code'
            ? `${API_EDU_PREFIX}/student/study-assistant`
            : `${API_EDU_PREFIX}/generate/study-assistant`;
        const response = await apiFetch(path, {
            method: 'POST',
            body: JSON.stringify({
                messages,
                ...(opts?.role && EDUAPP_AUTH_TYPE !== 'student_code' ? { role: opts.role } : {}),
                ...(opts?.context ? { context: opts.context } : {}),
            })
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error((err as { message?: string }).message || 'Không thể kết nối Trợ lý học tập.');
        }
        const data = await response.json();
        await refreshCreditBalance('study-assistant');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "Trợ lý học tập");
    }
};

// ── Bộ nhớ cá nhân hóa của Trợ lý AI (minh bạch: xem & xóa) ──────────────────

export interface AssistantMemoryFact {
    id: number;
    content: string;
    source: 'ai' | 'user';
    updatedAt: string | null;
}

export interface AssistantMemoryData {
    enabled: boolean;
    /** Khối hồ sơ (phái sinh + ghi nhớ) đúng như AI nhìn thấy. */
    profile: string;
    facts: AssistantMemoryFact[];
}

export const getAssistantMemory = async (role: 'student' | 'teacher' | 'parent'): Promise<AssistantMemoryData> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/generate/study-assistant/memory?role=${role}`, { method: 'GET' });
    if (!response.ok) throw new Error('Không thể tải bộ nhớ Trợ lý AI.');
    return await response.json();
};

export const addAssistantMemoryFact = async (
    content: string,
    role: 'student' | 'teacher' | 'parent',
): Promise<AssistantMemoryFact | null> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/generate/study-assistant/memory`, {
        method: 'POST',
        body: JSON.stringify({ content, role }),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Không thể thêm ghi nhớ.');
    }
    const data = await response.json();
    return data.fact ?? null;
};

export const deleteAssistantMemoryFact = async (id: number): Promise<void> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/generate/study-assistant/memory/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Không thể xóa ghi nhớ.');
};

export const clearAssistantMemory = async (role?: 'student' | 'teacher' | 'parent'): Promise<void> => {
    const path = `${API_EDU_PREFIX}/generate/study-assistant/memory${role ? `?role=${role}` : ''}`;
    const response = await apiFetch(path, { method: 'DELETE' });
    if (!response.ok) throw new Error('Không thể xóa bộ nhớ Trợ lý AI.');
};

export const generateEnglishChat = async (messages: { role: 'user' | 'ai', text: string }[], level?: string): Promise<any> => {
    const payload = { messages, level };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/english-chat`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi trò chuyện tiếng Anh.');
        const data = await response.json();
        await refreshCreditBalance('english-chat');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "trò chuyện tiếng Anh từ máy chủ");
    }
};

export const generateSmartDesign = async (template: string, prompt: string, title?: string, referenceImage?: string, contentFile?: string, contentUrl?: string, contentText?: string): Promise<any> => {
    const payload = { template, prompt, title, referenceImage, contentFile, contentUrl, contentText };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/smart-design`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi thiết kế thông minh.');
        const data = await response.json();
        await refreshCreditBalance('smart-design');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "thiết kế thông minh từ máy chủ");
    }
};

export const enhanceImage = async (image: string, prompt: string): Promise<string> => {
    const payload = { image, prompt };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/image-enhance`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi nâng cấp hình ảnh.');
        const data = await response.json();
        await refreshCreditBalance('image-enhance');
        return data.imageUrl;
    } catch (error) {
        throw handleGeminiError(error, "nâng cấp hình ảnh từ máy chủ");
    }
};

export const formatAssetUrl = (url?: string): string => {
    return resolvePublicFileUrl(url);
};

export const initializeVideoProject = async (script: string): Promise<any> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video-project`, {
            method: 'POST',
            body: JSON.stringify({ script })
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi khởi tạo dự án video.');
        const data = await response.json();
        await refreshCreditBalance('video-project-init');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "khởi tạo dự án video");
    }
};

export const generateCreativeVideo = async (scenes: any[], characters: any[], script?: string, projectId?: number, force: boolean = false): Promise<any> => {
    const payload = { scenes, characters, script, projectId, isSceneBased: true, force };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo video.');
        const data = await response.json();
        await refreshCreditBalance('video-project');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo video từ máy chủ");
    }
};

export const generateSingleScene = async (projectId: number | string, sceneId: string, force: boolean = false): Promise<any> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video-projects/${projectId}/generate-scene`, {
            method: 'POST',
            body: JSON.stringify({ sceneId, force })
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo cảnh lẻ.');
        const data = await response.json();
        await refreshCreditBalance('video-scene');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo cảnh video đơn lẻ");
    }
};

export const fetchSceneResult = async (projectId: number | string, sceneId: string): Promise<any> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video-projects/${projectId}/fetch-scene-result`, {
            method: 'POST',
            body: JSON.stringify({ sceneId })
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi lấy kết quả cảnh.');
        return await response.json();
    } catch (error) {
        throw handleGeminiError(error, "lấy kết quả cảnh video");
    }
};

export const mergeVideoProject = async (projectId: number | string): Promise<any> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video-projects/${projectId}/merge`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi ghép video.');
        return await response.json();
    } catch (error) {
        throw handleGeminiError(error, "ghép các cảnh video");
    }
};

export const uploadCharacterImage = async (file: File): Promise<{ imageUrl: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    try {
        const response = await fetch(apiUrl(`${API_EDU_PREFIX}/generate/upload-character`), {
            method: 'POST',
            headers: {
                ...authHeader(),
                'Accept': 'application/json',
            },
            body: formData,
            credentials: 'include'
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tải ảnh lên.');
        const res = await response.json();
        return res;
    } catch (error) {
        throw handleGeminiError(error, "tải ảnh nhân vật lên");
    }
};

/**
 * Tải một ảnh lên server và trả về URL.
 */
export const uploadImageResource = async (file: File): Promise<string> => {
    const res = await uploadCharacterImage(file);
    return res.imageUrl;
};

export const generateGameFrameworkContent = async (
    gradeLevel: GradeLevel,
    gameType: GameType,
    topic: string,
    detailedInstruction: string,
    contextContent: string | null,
    allowAiAugmentation: boolean,
    sourceLessonPlan?: LessonPlan | null,
  settings?: Record<string, any>,
  aiProvider: AIProvider = 'gemini',
  id?: string | number,
  extraOptions?: { subject?: string; classLevel?: string; textbookSet?: string; sourceDocumentId?: string | number }
): Promise<Game> => {
    const payload = { gradeLevel, gameType, topic, detailedInstruction, contextContent, allowAiAugmentation, sourceLessonPlan, settings, aiProvider, id, ...extraOptions };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/game-content`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo game.');
        const jsonResponse = await response.json();
        await refreshCreditBalance('game');
        // Flatten if nested under 'data'
        const result = jsonResponse?.data && (jsonResponse.data.content || jsonResponse.data.title) ? { ...jsonResponse, ...jsonResponse.data } : jsonResponse;
        
        // Ensure title exists and metadata is merged
        return { 
            ...result, 
            title: result.title || topic || gameType || 'Trò chơi mới',
            gradeLevel, 
            gameType, 
            topic 
        };
    } catch (error) {
        throw handleGeminiError(error, "tạo game từ máy chủ");
    }
};

export const generateChildLearningPlan = async (
    age: number,
    gender: 'male' | 'female' | 'other',
    goal: string,
    difficulty: PlanDifficulty,
    duration: PlanDuration,
    fileContext: string | null
): Promise<Pick<ChildLearningPlan, 'title' | 'dailyPlans'>> => {
    const payload = { age, gender, goal, difficulty, duration, fileContext };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/child-plan`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo lộ trình học tập.');
        const data = await response.json();
        await refreshCreditBalance('child-plan');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo lộ trình học tập từ máy chủ");
    }
};

export const generateHistoryStory = async (event: string, perspective: string, style: string, age: string): Promise<HistoryStory> => {
    const payload = { event, perspective, style, age };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/history-story`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo truyện lịch sử.');
        const data = await response.json();
        await refreshCreditBalance('history-story');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo truyện lịch sử từ máy chủ");
    }
};

export const generateLiteratureAnalysis = async (inputText: string): Promise<LiteratureAnalysisGuide> => {
    const payload = { inputText };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/literature-analysis`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo gợi ý văn học.');
        const data = await response.json();
        await refreshCreditBalance('literature-analysis');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo gợi ý văn học từ máy chủ");
    }
};

export const generateScienceExplanation = async (concept: string, age: string): Promise<ScienceExplanation> => {
    const payload = { concept, age };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/science-explanation`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi giải thích khoa học.');
        const data = await response.json();
        await refreshCreditBalance('science-explanation');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "giải thích khoa học từ máy chủ");
    }
};

/** Payload gợi ý «Phạm vi kiến thức & Chuẩn KT-KN» — các trường mở rộng giúp AI bám SGK / ngữ cảnh lớp. */
export type SampleDataPayload = {
    title: string;
    subject: string;
    gradeLevel: GradeLevel;
    schoolYear?: string;
    textbookSet?: string;
    textbookChapter?: string;
    textbookLesson?: string;
    classLevel?: string;
    purposePreset?: string;
};

export const generateSampleData = async (payload: SampleDataPayload): Promise<{ text: string }> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/sample-data`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo dữ liệu mẫu.');
        const data = await response.json();
        await refreshCreditBalance('sample-data');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo dữ liệu mẫu từ máy chủ");
    }
};

export const generateTestMatrix = async (
    gradeLevel: GradeLevel, 
    subject: string, 
    topics: string[], 
    duration: number, 
    category: QuizCategory, 
    contextContent: string | null, 
    allowAiAugmentation: boolean, 
    constraints?: any,
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraData?: Partial<Quiz>
): Promise<{ matrix: MatrixRow[] }> => {
    const payload = { 
        gradeLevel, subject, topics, duration, category, contextContent, 
        allowAiAugmentation, constraints, aiProvider, id,
        ...extraData 
    };
    if ((payload as any).sourceDocumentIds && !(payload as any).source_document_ids) {
        (payload as any).source_document_ids = (payload as any).sourceDocumentIds;
    }
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/test-matrix`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo ma trận.');
        const data = await response.json();
        await refreshCreditBalance('test-matrix');
        return data;
    } catch (e) { throw handleGeminiError(e, "tạo ma trận từ máy chủ"); }
};

export const normalizeScopeAndStandards = async (text: string): Promise<{ text: string }> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/normalize-scope`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify({ text })
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi chuẩn hóa nội dung.');
        return await response.json();
    } catch (e) { throw handleGeminiError(e, "chuẩn hóa nội dung từ máy chủ"); }
};

export const generateTestSpecification = async (
    gradeLevel: GradeLevel, 
    subject: string, 
    matrix: MatrixRow[], 
    contextContent: string | null, 
    allowAiAugmentation: boolean,
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraData?: Partial<Quiz>
): Promise<{ specification: SpecificationTopic[] }> => {
    const payload = { 
        gradeLevel, subject, matrix, contextContent, allowAiAugmentation, 
        aiProvider, id,
        ...extraData 
    };
    if ((payload as any).sourceDocumentIds && !(payload as any).source_document_ids) {
        (payload as any).source_document_ids = (payload as any).sourceDocumentIds;
    }
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/test-specification`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo bản đặc tả.');
        const data = await response.json();
        await refreshCreditBalance('test-specification');
        return data;
    } catch (e) { throw handleGeminiError(e, "tạo bản đặc tả từ máy chủ"); }
};

export const generateQuizFromMatrix = async (
    gradeLevel: GradeLevel, 
    subject: string, 
    title: string, 
    matrix: MatrixRow[], 
    specification: SpecificationTopic[], 
    duration: number, 
    contextContent: string | null, 
    allowAiAugmentation: boolean,
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraData?: Partial<Quiz>
): Promise<Quiz> => {
    const payload = { 
        gradeLevel, subject, title, matrix, specification, duration, 
        contextContent, allowAiAugmentation, aiProvider, id,
        ...extraData 
    };
    if ((payload as any).sourceDocumentIds && !(payload as any).source_document_ids) {
        (payload as any).source_document_ids = (payload as any).sourceDocumentIds;
    }
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/quiz-from-matrix`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo đề.');
        const data = await response.json();
        await refreshCreditBalance('quiz-from-matrix');
        return data;
    } catch (e) { throw handleGeminiError(e, "tạo đề từ máy chủ"); }
};

export const generateSimpleQuiz = async (
    gradeLevel: GradeLevel, 
    subject: string, 
    title: string, 
    scope: string, 
    duration: number, 
    context: string | null, 
    allowAugmentation: boolean, 
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraData?: Partial<Quiz>
): Promise<Quiz> => {
    const payload = { 
        gradeLevel, subject, title, scope, duration, context, 
        allowAugmentation, aiProvider, id,
        ...extraData 
    };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/simple-quiz`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            const message = (typeof body?.message === 'string' && body.message.trim())
                ? body.message
                : (typeof body?.error === 'string' && body.error.trim())
                    ? body.error
                    : 'Lỗi từ máy chủ khi tạo đề nhanh.';
            const err = new Error(message);
            if (body?.code) (err as any).code = body.code;
            throw err;
        }
        const data = await response.json();
        await refreshCreditBalance('simple-quiz');
        return data;
    } catch (e) { throw handleGeminiError(e, "tạo đề nhanh từ máy chủ"); }
};

export const generateQuizForModel = async (
    model: TeachingModel, 
    config: any, 
    gradeLevel: GradeLevel, 
    subject: string, 
    title: string, 
    scope: string, 
    duration: number, 
    context: string | null, 
    allowAugmentation: boolean,
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraData?: Partial<Quiz>
): Promise<Quiz> => {
    const payload = { 
        model, config, gradeLevel, subject, title, scope, duration, 
        context, allowAugmentation, aiProvider, id,
        ...extraData 
    };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/model-quiz`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo bài đánh giá.');
        const data = await response.json();
        await refreshCreditBalance('model-quiz');
        return data;
    } catch (e) { throw handleGeminiError(e, "tạo bài đánh giá từ máy chủ"); }
};

export const generateVideo = async (prompt: string): Promise<GenerateVideosOperation> => {
    const payload = { prompt };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/video`, {
            method: 'POST',
            headers: {},
            body: JSON.stringify(payload)
        });
        if (response.status !== 202) throw new Error('Lỗi từ máy chủ khi khởi tạo video.');
        const data = await response.json();
        await refreshCreditBalance('video');
        return data; 
    } catch (error) {
        throw handleGeminiError(error, "khởi tạo video từ máy chủ");
    }
};

/**
 * Proxies multiple image URLs through the backend to get their base64 representation in batch.
 * Useful for bypassing CORS when generating PPTX/PDF.
 */
export const fetchImagesAsBase64Batch = async (imageUrls: string[]): Promise<Record<string, string>> => {
    if (imageUrls.length === 0) return {};

    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/proxy-base64-batch`, {
            method: 'POST',
            body: JSON.stringify({ urls: imageUrls })
        });
        if (!response.ok) return imageUrls.reduce((acc, url) => ({ ...acc, [url]: url }), {});
        const data = await response.json();
        return data.results || {};
    } catch (error) {
        console.error("Error fetching base64 proxy batch:", error);
        return imageUrls.reduce((acc, url) => ({ ...acc, [url]: url }), {});
    }
};

/**
 * Single version of the proxy helper.
 */
export const fetchImageAsBase64 = async (imageUrl: string): Promise<string> => {
    const results = await fetchImagesAsBase64Batch([imageUrl]);
    return results[imageUrl] || imageUrl;
};

export const getVideoOperation = async (operationName: string): Promise<GenerateVideosOperation> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/operations/video/${operationName.split('/').pop()}`, {
            method: 'GET',
            headers: {},
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi kiểm tra tiến trình video.');
        return await response.json();
    } catch (error) {
        throw handleGeminiError(error, "kiểm tra tiến trình video từ máy chủ");
    }
};

export const saveVideoProject = async (project: Omit<VideoProject, 'id' | 'createdAt'>): Promise<VideoProject> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/video-projects`, {
        method: 'POST',
        body: JSON.stringify({
            name: project.name,
            source_activity_id: project.sourceActivityId,
            scenes: project.scenes,
            characters: project.characters
        })
    });
    if (!res.ok) throw new Error('Không lưu được dự án video');
    const data = await res.json();
    return data.data || data;
};

export const updateVideoProject = async (project: VideoProject): Promise<void> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/video-projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
            name: project.name,
            source_activity_id: project.sourceActivityId,
            scenes: project.scenes,
            characters: project.characters
        })
    });
    if (!res.ok) throw new Error('Không cập nhật được dự án video');
};

export const generateInteractiveVideoScript = async (videoUrl: string, prompt: string, context?: any): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/generate/interactive-video`, {
        method: 'POST',
        body: JSON.stringify({ videoUrl, prompt, context })
    });
    if (!res.ok) throw new Error('Lỗi khi AI tạo kịch bản tương tác');
    const data = await res.json();
    await refreshCreditBalance('interactive_video');
    return data;
};

// =================================================================================
// USER DATA MANAGEMENT APIs (PRODUCTION)
// =================================================================================

/**
 * Generic fetch function for any resource.
 */
export const fetchResource = async (resource: string): Promise<any[]> => {
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/${resource}`);
        if (!response.ok) return [];
        return await response.json();
    } catch (error) {
        console.error(`Error fetching ${resource}:`, error);
        return [];
    }
};

/**
 * @deprecated Use individual fetchers or fetchResource
 */
export const fetchAllUserData = async (resources?: string): Promise<Record<string, any[]> | null> => {
    if (APP_MODE === 'PRODUCTION') {
        // Purpose: Fetch user-related data (documents, lesson plans, etc.) from the backend.
        // Student code login: dùng endpoint student/data riêng (không user, không credit)
        try {
            const isStudentCode = EDUAPP_AUTH_TYPE === 'student_code';
            const path = isStudentCode ? `${API_EDU_PREFIX}/student/data` : `${API_EDU_PREFIX}/user/data${resources ? `?resources=${resources}` : ''}`;
            const response = await apiFetch(path, {
                method: 'GET',
                headers: {},
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Lỗi không xác định từ máy chủ.' }));
                throw new Error(errorData.message || 'Lỗi từ máy chủ khi tải dữ liệu người dùng.');
            }
            const jsonResponse = await response.json();
            // Hỗ trợ cả trường hợp backend trả về trực tiếp hoặc bọc trong data (phòng lỗi double wrapping)
            const userData = jsonResponse.data ? (jsonResponse.data.data ? jsonResponse.data.data : jsonResponse.data) : jsonResponse;
            
            // Chỉ lưu localStorage khi tải đầy đủ (không phải partial như joinRequests)
            // Tránh ghi đè dữ liệu đầy đủ bằng partial → mất classes khi refresh
            const isFullLoad = !resources || resources === '' || resources === 'all';
            if (userData && CURRENT_USER_ID && isFullLoad) {
                saveUserDataToLocal(CURRENT_USER_ID, userData);
            }
            
            return userData;
        } catch (error) {
            throw handleGeminiError(error, "tải dữ liệu người dùng từ máy chủ");
        }
    }
    // In DEVELOPMENT mode, data is handled by the useLocalStorage hook in App.tsx.
    return Promise.resolve(null);
};

/**
 * Helper để tạo link chia sẻ SEO friendly.
 */
export const getShareLink = (resource: string, id: string | number, title: string, token: string | null): string => {
    const slug = title.toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[đĐ]/g, 'd')
        .replace(/([^0-9a-z-\s])/g, '')
        .replace(/(\s+)/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
    
    const RESOURCE_URL_MAP: Record<string, string> = {
        'lesson-plans': 'giao-an',
        'drawing-lessons': 'bai-hoc-ve',
        'quizzes': 'de-kiem-tra',
        'games': 'tro-choi',
        'presentations': 'trinh-chieu',
        'scientific-simulations': 'mo-phong-khoa-hoc',
        'homeworks': 'bai-tap',
        'activities': 'hoat-dong',
        'documents': 'tai-lieu',
        'video-projects': 'video-tuong-tac',
        'thiet-ke-3d': 'thiet-ke-3d',
        'ai-fact-check-sessions': 'kiem-chung-ai',
        'ai-ethics-challenge-sessions': 'thu-thach-dao-duc-ai',
        'ai-lab-sessions': 'phong-lab-ai',
    };
    
    const resType = RESOURCE_URL_MAP[resource] || resource;
    const slugWithId = `${slug || 'tai-nguyen'}-i${id}.html`;
    
    let url = `${window.location.origin}/chia-se/${resType}/${slugWithId}`;
    if (token) {
        url += `?token=${token}`;
    }
    return url;
};

/**
 * Cập nhật visibility và link chia sẻ.
 * @param resource - resource type (games, lesson-plans, quizzes, ...)
 * @param id - resource id
 * @param payload - { visibility, shareExpiresAt?, shareScope? }
 */
export const updateResourceVisibility = async (
    resource: string,
    id: string | number,
    payload: { visibility: 'private' | 'link' | 'public' | 'restricted'; shareExpiresAt?: string | null; shareScope?: object }
): Promise<any> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/${encodeURIComponent(resource)}/${encodeURIComponent(id)}/visibility`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
    });
    if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Lỗi cập nhật chia sẻ'));
    }
    return response.json();
};

/**
 * Lấy danh sách nhóm tôi sở hữu (cho chia sẻ restricted).
 */
export const fetchGroupsMine = async (): Promise<{ id: number; name: string }[]> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/groups/mine`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.groups || [];
};

/** Tạo nhóm Bookai thêm (khi chưa vượt giới hạn gói / admin). */
export const createGroupBookai = async (name: string): Promise<{ id: number; name: string }> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/group-credits/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error((data as any).message || 'Không thể tạo nhóm');
    }
    return (data as any).group;
};

/**
 * Tìm kiếm người dùng theo email (cho chia sẻ restricted).
 */
export const searchUsersByEmail = async (q: string): Promise<{ id: number; email: string; name?: string }[]> => {
    if (!q || q.length < 2) return [];
    const response = await apiFetch(`${API_EDU_PREFIX}/users/search?q=${encodeURIComponent(q)}`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.users || [];
};

/**
 * Lấy tài nguyên được chia sẻ với tôi (restricted scope).
 */
export const fetchSharedWithMe = async (): Promise<Record<string, any[]>> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/shared/with-me`);
    if (!response.ok) return {};
    return response.json();
};

/**
 * Lấy danh sách tài nguyên công khai từ thư viện cộng đồng.
 * @param type - Lọc theo loại (games, lesson-plans, ...)
 * @param page - Trang phân trang
 * @param limit - Số item mỗi trang
 * @param q - Từ khóa tìm kiếm
 */
export const fetchSharedLibrary = async (params?: {
    type?: string;
    page?: number;
    limit?: number;
    q?: string;
    targetRole?: string;
}): Promise<{ items: any[]; page: number; limit: number; hasMore: boolean; total?: number }> => {
    const sp = new URLSearchParams();
    if (params?.type) sp.set('type', params.type);
    if (params?.page) sp.set('page', String(params.page));
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.q?.trim()) sp.set('q', params.q.trim());
    if (params?.targetRole) sp.set('targetRole', params.targetRole);
    const qs = sp.toString();
    const url = `${API_BASE_URL}${API_EDU_PREFIX}/shared/library${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Không thể tải thư viện.');
    const data = await response.json();
    return {
        items: data.items || [],
        page: data.page ?? 1,
        limit: data.limit ?? 24,
        hasMore: data.hasMore ?? false,
    };
};

/**
 * Đánh giá tài nguyên (1-5 sao). Cần auth.
 */
export const rateSharedResource = async (
    resource: string,
    id: string | number,
    rating: number
): Promise<{ rating: number; ratingAvg: number; ratingCount: number }> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/shared/${resource}/${id}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating }),
    });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể đánh giá');
    }
    return response.json();
};

/**
 * Sao chép tài nguyên được chia sẻ vào thư viện của tôi (cần auth).
 */
export const copySharedResourceToMyLibrary = async (
    resource: string,
    id: string | number,
    token?: string | null
): Promise<any> => {
    const url = token
        ? `${API_EDU_PREFIX}/shared/${resource}/${id}/copy?token=${encodeURIComponent(token)}`
        : `${API_EDU_PREFIX}/shared/${resource}/${id}/copy`;
    const response = await apiFetch(url, { method: 'POST' });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể sao chép tài nguyên');
    }
    return response.json();
};

/**
 * Nhân bản một học liệu của chính mình (Kho học liệu → nút "Sao chép").
 */
export const duplicateResource = async (resource: string, id: string | number): Promise<any> => {
    const response = await apiFetch(`${API_EDU_PREFIX}/${resource}/${id}/duplicate`, { method: 'POST' });
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Không thể sao chép');
    }
    return response.json();
};

/**
 * Lấy tài nguyên qua link chia sẻ (không cần auth).
 */
export const fetchSharedResource = async (
    resource: string,
    id: string | number,
    token?: string | null
): Promise<any> => {
    const url = token
        ? `${API_BASE_URL}${API_EDU_PREFIX}/shared/${resource}/${id}?token=${encodeURIComponent(token)}`
        : `${API_BASE_URL}${API_EDU_PREFIX}/shared/${resource}/${id}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Lỗi tải tài nguyên'));
    }
    return response.json();
};

/**
 * Lấy chi tiết một tài nguyên theo ID (PRODUCTION)
 */
/**
 * Lấy chi tiết một tài nguyên theo ID (PRODUCTION)
 */
export const fetchResourceById = async (resource: string, id: string | number, forceRefresh: boolean = false): Promise<any | null> => {
    if (APP_MODE !== 'PRODUCTION') return null;
    try {
        // Chỉ thêm timestamp để tránh cache trình duyệt khi thực sự cần thiết (ví dụ: sau khi sửa)
        const url = forceRefresh 
            ? `${API_EDU_PREFIX}/${resource}/${id}?_t=${Date.now()}`
            : `${API_EDU_PREFIX}/${resource}/${id}`;
            
        const response = await apiFetch(url, forceRefresh ? {
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
            },
        } : undefined);
        if (!response.ok) return null;
        const result = await response.json();
        // Flatten nested data if present (common pattern in Laravel/other backends)
        return result?.data && (result.data.id || result.data._id) ? { ...result, ...result.data } : result;
    } catch (error) {
        console.error(`Error fetching ${resource} with id ${id}:`, error);
        return null;
    }
};

/**
 * Performs an API sync operation with optimistic update to localStorage and rollback on failure.
 */
export const syncWithRollback = async (
    resource: string, 
    method: 'POST' | 'PATCH' | 'DELETE', 
    data: any, 
    id?: string | number,
    onStateUpdate?: (updatedData: any, action: 'add' | 'update' | 'delete') => void
): Promise<any> => {
    if (APP_MODE !== 'PRODUCTION') return null;
    
    // 1. Prepare for rollback (save current state if needed - usually done via local state management)
    // In this simple implementation, we rely on the caller to provide onStateUpdate for optimistic UI.
    
    const action = method === 'POST' ? 'add' : (method === 'DELETE' ? 'delete' : 'update');
    
    // 2. Optimistic Update Local Storage
    if (CURRENT_USER_ID) {
        updateLocalResource(CURRENT_USER_ID, resource, data, action);
    }
    
    // 3. Optimistic Update React State (via callback)
    if (onStateUpdate) {
        onStateUpdate(data, action);
    }

    // 4. Perform API Sync
    try {
        const response = await syncResource(resource, method, data, id);
        
        if (!response) {
            throw new Error("Sync failed - no response");
        }
        
        // If it was a POST, we might get a real ID back. Update Local Storage and State again.
        if (method === 'POST' && response.id && CURRENT_USER_ID) {
            updateLocalResource(CURRENT_USER_ID, resource, { ...data, ...response }, 'update');
            if (onStateUpdate) onStateUpdate({ ...data, ...response }, 'update');
        }
        
        return response;
    } catch (error) {
        console.error(`Sync error for ${resource} (${method}), rolling back...`, error);
        
        // 5. ROLLBACK
        if (CURRENT_USER_ID) {
            // If it was an add, delete it. If it was a delete/update, we'd need the original data for a full rollback.
            // For simplicity, we'll just log the failure and perhaps show a toast in UI.
            // A full rollback would require passing the previousItem.
        }
        
        throw error; // Rethrow so the UI can handle the error (e.g., show toast)
    }
};

/** Tải danh sách con của phụ huynh hiện tại. */
export const getChildren = async (): Promise<any[]> => {
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/children`, { method: 'GET' });
        if (!res.ok) return [];
        const data = await res.json();
        return Array.isArray(data) ? data : (data.data ?? []);
    } catch {
        return [];
    }
};

/**
 * Generic sync function for any resource.
 */
export const syncResource = async (resource: string, method: 'POST' | 'PATCH' | 'DELETE', data?: any, id?: string | number): Promise<any> => {
    const url = id ? `${API_EDU_PREFIX}/${resource}/${id}` : `${API_EDU_PREFIX}/${resource}`;
    try {
        const response = await apiFetch(url, {
            method,
            body: data ? JSON.stringify(data) : undefined,
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Sync failed for ${resource} (${method}): Status ${response.status}`, errorText);
            throw new Error(`Failed to sync ${resource}: ${response.status}`);
        }
        const json = await response.json();
        // Breadcrumb: báo cho Deep Lesson Canvas biết một học liệu vừa được TẠO MỚI
        // (để liên kết ngược resourceId vào node đang chờ). An toàn/additive.
        if (method === 'POST') {
            const createdId = json?.id ?? json?.data?.id;
            if (createdId) {
                try {
                    const crumb = {
                        resource,
                        id: String(createdId),
                        title: json?.title ?? json?.data?.title ?? data?.title ?? '',
                        ts: Date.now(),
                    };
                    localStorage.setItem('eduapp_last_created_resource', JSON.stringify(crumb));
                    window.dispatchEvent(new CustomEvent('eduapp-resource-created', { detail: crumb }));
                } catch { /* ignore */ }
            }
        }
        return json;
    } catch (error) {
        console.error(`Sync error (${resource}):`, error);
        return null;
    }
};

/**
 * Saves all of the user's data to the backend in PRODUCTION mode.
 * @deprecated Use syncResource for individual updates.
 */
export const saveAllUserData = async (data: Record<string, any[]>): Promise<boolean> => {
    if (APP_MODE === 'PRODUCTION') {
        try {
            const response = await apiFetch(`${API_EDU_PREFIX}/user/data`, {
                method: 'POST',
                body: JSON.stringify({ data }),
            });
            if (response.ok) {
            } else {
                console.error("Save User Data Failed:", response.status);
            }
            return response.ok;
        } catch (error) {
            console.error("Failed to save user data to backend:", error);
            return false;
        }
    }
    return true;
};

// ==========================================
// User Settings (PRODUCTION)
// ==========================================
export const getUserSettings = async (): Promise<any> => {
    if (APP_MODE !== 'PRODUCTION') return Promise.resolve(null);
    const res = await apiFetch(`${API_EDU_PREFIX}/user/settings`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được user settings');
    const data = await res.json();
    return data.settings;
};

export const updateUserSettings = async (settings: any): Promise<boolean> => {
    if (APP_MODE !== 'PRODUCTION') return Promise.resolve(true);
    const res = await apiFetch(`${API_EDU_PREFIX}/user/settings`, {
        method: 'PATCH',
        body: JSON.stringify({ settings })
    });
    if (!res.ok) throw new Error('Không cập nhật được user settings');
    return true;
};

/** ID thông báo có thể cập nhật/xóa (từ edu_notifications). Announcements (ann_*) không lưu DB. */
const isEditableNotificationId = (id: string): boolean => /^\d+$/.test(String(id));

/** Đánh dấu một thông báo đã đọc (lưu DB). */
export const markNotificationAsRead = async (id: string): Promise<void> => {
    if (APP_MODE !== 'PRODUCTION' || !isEditableNotificationId(id)) return;
    const res = await apiFetch(`${API_EDU_PREFIX}/notifications/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isRead: true })
    });
    if (!res.ok) throw new Error('Không thể đánh dấu đã đọc');
};

/** Đánh dấu tất cả thông báo đã đọc (lưu DB). */
export const markAllNotificationsAsRead = async (notificationIds: string[]): Promise<void> => {
    if (APP_MODE !== 'PRODUCTION') return;
    const editableIds = notificationIds.filter(isEditableNotificationId);
    await Promise.all(editableIds.map(id => apiFetch(`${API_EDU_PREFIX}/notifications/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isRead: true })
    })));
};

/** Xóa các thông báo đã đọc khỏi DB. */
export const deleteReadNotifications = async (readNotificationIds: string[]): Promise<void> => {
    if (APP_MODE !== 'PRODUCTION') return;
    const editableIds = readNotificationIds.filter(isEditableNotificationId);
    await Promise.all(editableIds.map(id => apiFetch(`${API_EDU_PREFIX}/notifications/${id}`, { method: 'DELETE' })));
};

/** Upload avatar cho user. Trả về URL avatar đã format. */
export const uploadUserAvatar = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await fetch(apiUrl(`${API_EDU_PREFIX}/user/avatar`), {
        method: 'POST',
        headers: { ...authHeader(), 'Accept': 'application/json' },
        body: formData,
        credentials: 'include'
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as any)?.message || 'Không thể tải avatar lên.');
    }
    const data = await response.json();
    const url = (data as any)?.avatar;
    return url ? (formatAssetUrl(url) || url) : '';
};

/** Phụ huynh: Liên kết với tài khoản học sinh (nhập email con) */
export const linkStudentAccount = async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/user/link-student`, {
            method: 'POST',
            body: JSON.stringify({ email: email.trim() })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { success: false, message: (data as any)?.message || 'Không thể liên kết.' };
        return { success: true, message: (data as any)?.message || 'Đã liên kết thành công.' };
    } catch (e: any) {
        return { success: false, message: e?.message || 'Lỗi kết nối.' };
    }
};

/** Học sinh: Tìm phụ huynh theo email để liên kết */
export const searchParentForLink = async (email: string): Promise<{ success: boolean; children: { child_id: number; child_name: string; student_id: number; student_name: string }[]; message?: string }> => {
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/user/link-parent-search?email=${encodeURIComponent(email.trim())}`, { method: 'GET' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { success: false, children: [], message: (data as any)?.message };
        return { success: true, children: (data as any)?.children || [], message: (data as any)?.message };
    } catch (e: any) {
        return { success: false, children: [], message: e?.message || 'Lỗi kết nối.' };
    }
};

/** Học sinh: Xác nhận liên kết với hồ sơ học sinh */
export const linkParentConfirm = async (studentId: number): Promise<{ success: boolean; message: string }> => {
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/user/link-parent-confirm`, {
            method: 'POST',
            body: JSON.stringify({ student_id: studentId })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { success: false, message: (data as any)?.message || 'Không thể liên kết.' };
        return { success: true, message: (data as any)?.message || 'Đã liên kết thành công.' };
    } catch (e: any) {
        return { success: false, message: e?.message || 'Lỗi kết nối.' };
    }
};

// ==========================================
// Ads Config (PRODUCTION)
// ==========================================
export const getAdsConfig = async (): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/ads/config`, { method: 'GET' });
    if (!res.ok) throw new Error('Không tải được cấu hình quảng cáo');
    return await res.json();
};

/** Lấy danh sách banner popup (public - cả khi chưa đăng nhập) */
export const getBanners = async (): Promise<Banner[]> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/banners`, { method: 'GET' });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
};

/** Lấy thông báo hệ thống (public - cả khi chưa đăng nhập) */
export const getAnnouncements = async (): Promise<any[]> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/announcements`, { method: 'GET' });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
};

// ==========================================
// Credits (PRODUCTION)
// ==========================================
export const getCreditBalance = async (): Promise<number> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/balance`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được số dư credit');
    const data = await res.json();
    return data.balance as number;
};

export const getCreditPackages = async (): Promise<any[]> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/packages`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được gói credit');
    const data = await res.json();
    return data.packages as any[];
};

/**
 * Lay packages kem `careerJourneyBaseline` (admin free quota baseline).
 *
 * Career Journey: pkg.max_children/max_saved_careers la DELTA so voi free baseline.
 *  - Effective max children = baseline.maxChildren + pkg.max_children
 *  - Effective max saved careers = baseline.maxSavedCareers + pkg.max_saved_careers
 *
 * Dung trong UI Cua hang Credit de hien thi quota hieu luc thay vi raw delta.
 */
export const getCreditPackagesWithBaseline = async (): Promise<{
    packages: any[];
    careerJourneyBaseline: { maxChildren: number; maxSavedCareers: number };
}> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/packages`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được gói credit');
    const data = await res.json();
    return {
        packages: (data.packages || []) as any[],
        careerJourneyBaseline: {
            maxChildren: Number(data?.careerJourneyBaseline?.maxChildren ?? 2),
            maxSavedCareers: Number(data?.careerJourneyBaseline?.maxSavedCareers ?? 3),
        },
    };
};

export const previewCreditCoupon = async (packageId: string | number, couponCode: string): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/coupons/preview`, {
        method: 'POST',
        body: JSON.stringify({ packageId, couponCode }),
    });
    if (!res.ok) throw new Error(await readErrorMessage(res, 'Không áp dụng được mã ưu đãi'));
    return await res.json();
};

export const purchaseCreditPackage = async (packageId: number, provider?: string, couponCode?: string): Promise<{ orderId: number; status: string; }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/purchase`, {
        method: 'POST',
        body: JSON.stringify({ packageId, provider, couponCode }),
    });
    if (!res.ok) throw new Error('Không thể mua gói credit');
    return await res.json();
};

export const getCreditHistory = async (page: number = 1, limit: number = 20): Promise<{ data: any[], total: number, last_page: number, current_page: number }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/history?page=${page}&limit=${limit}`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được lịch sử credit');
    return await res.json();
};

export const getCreditOrders = async (page: number = 1, limit: number = 20): Promise<{ data: any[], total: number, last_page: number, current_page: number }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/orders?page=${page}&limit=${limit}`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được danh sách đơn hàng');
    return await res.json();
};

export const getOrderDetails = async (orderId: number): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/order/${orderId}`, { method: 'GET' });
    if (!res.ok) throw new Error('Không lấy được thông tin đơn hàng');
    return await res.json();
};

/** Xác nhận thanh toán PayOS từ redirect (khi user quay về với status=PAID) */
export const confirmPayosRedirect = async (params: { orderCode: number; status?: string; code?: string; cancel?: string }): Promise<{ success: boolean; credits?: number; message?: string; alreadyProcessed?: boolean; error_code?: string }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/credits/confirm-payos-redirect`, {
        method: 'POST',
        body: JSON.stringify(params),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.message || 'Không xác nhận được thanh toán') as Error & { status?: number; errorCode?: string };
        err.status = res.status;
        err.errorCode = data.error_code;
        throw err;
    }
    return data;
};

/** Gửi bằng chứng chuyển khoản thủ công (Bank/MoMo) */
export const confirmManualPayment = async (
    orderId: number,
    proofImage?: File | null,
    proofNote?: string
): Promise<{
    success: boolean;
    message: string;
    order?: {
        id: number;
        proof_image?: string | null;
        proof_note?: string | null;
    };
}> => {
    const formData = new FormData();
    formData.append('orderId', String(orderId));
    if (proofImage) {
        formData.append('proofImage', proofImage);
    }
    if (proofNote) formData.append('proofNote', proofNote);

    const authData = localStorage.getItem('EDUAPP_AUTH');
    const token = authData ? JSON.parse(authData).accessToken : null;

    const res = await fetch(apiUrl(`${API_EDU_PREFIX}/credits/confirm-manual`), {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData,
    });
    
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'Không gửi được bằng chứng');
    return data;
};

export const submitFeedback = async (rating: number, comment: string, feature?: string): Promise<{ success: boolean, message: string }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ rating, comment, feature, url: window.location.href })
    });
    if (!res.ok) {
        if (res.status === 429) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.message || 'Bạn đang gửi góp ý quá nhanh. Vui lòng thử lại sau ít phút.');
        }
        throw new Error('Không gửi được góp ý. Vui lòng thử lại sau.');
    }
    return await res.json();
};

// ==========================================
// Group Credits (PRODUCTION)
// ==========================================
export const getGroupCreditsSummary = async (): Promise<{ group: any; members: any[]; usedThisMonth: number; policy?: any; monthlyPool?: number; needsConfirmation?: boolean; cycle?: { start: string; end: string } }> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve({ group: null as any, members: [], usedThisMonth: 0 });
  const res = await apiFetch(`${API_EDU_PREFIX}/group-credits/summary`, { method: 'GET' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).message || 'Không tải được tổng quan nhóm');
  return data;
};

export const addGroupMember = async (memberEmail: string, monthlyQuota = 0): Promise<boolean> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve(true);
  const res = await apiFetch(`${API_EDU_PREFIX}/group-credits/members`, {
    method: 'POST',
    body: JSON.stringify({ member_email: memberEmail, monthly_quota: monthlyQuota })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).message || 'Không thêm được thành viên');
  return true;
};

export const removeGroupMember = async (memberEmail: string): Promise<boolean> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve(true);
  const res = await apiFetch(`${API_EDU_PREFIX}/group-credits/members`, {
    method: 'DELETE',
    body: JSON.stringify({ member_email: memberEmail })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).message || 'Không xóa được thành viên');
  return true;
};

export const shareGroupCredits = async (toEmail: string, amount: number): Promise<boolean> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve(true);
  const res = await apiFetch(`${API_EDU_PREFIX}/group-credits/share`, {
    method: 'POST',
    body: JSON.stringify({ to_email: toEmail, amount })
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || 'Không chia sẻ được credit');
  }
  return true;
};

export const confirmGroupMembers = async (): Promise<{ ok: boolean; result: any }> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve({ ok: true, result: {} });
  const res = await apiFetch(`${API_EDU_PREFIX}/group-credits/confirm-members`, { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any).message || 'Không thể xác nhận thành viên');
  return data;
};

// ==========================================
// Social Login (PRODUCTION) — gọi API trong Module EduApp: /api/edu/auth/*
// ==========================================
/** Đăng nhập nhanh học sinh: mã 8 ký tự (A-Z,0-9) + ngày/tháng sinh (DDMM). Có thể cần security_answer nếu lớp có câu hỏi bảo mật. */
export const studentQuickLogin = async (code: string, dobDigits: string, securityAnswer?: string): Promise<{ student: { id: number; name: string; email: string; studentId?: string; loginCode: string; dob?: string }; access_token: string }> => {
    const body: Record<string, string> = { code: code.toUpperCase(), dobDigits };
    if (securityAnswer !== undefined && securityAnswer !== '') body.security_answer = securityAnswer;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/student-login`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = (err as { message?: string }).message;
        if (res.status === 429) throw new Error(msg || 'Tài khoản tạm khóa. Vui lòng thử lại sau 15 phút.');
        if (res.status === 422 && (err as { requires_security_answer?: boolean }).requires_security_answer) {
            const e = new Error(msg || 'Lớp học yêu cầu trả lời câu hỏi bảo mật.') as Error & { requiresSecurityAnswer?: boolean; securityQuestion?: string };
            e.requiresSecurityAnswer = true;
            e.securityQuestion = (err as { security_question?: string }).security_question || '';
            throw e;
        }
        throw new Error(msg || 'Mã đăng nhập không chính xác hoặc đã hết hạn.');
    }
    const data = await res.json();
    const s = data.student;
    const token = data.access_token;
    if (!s || !token) throw new Error('Phản hồi không hợp lệ.');
    return {
        access_token: token,
        student: {
            id: s.id,
            name: s.name,
            email: s.email || `student_${code}@quick.login`,
            studentId: s.studentId,
            loginCode: s.loginCode,
            dob: s.dob,
        },
    };
};

/** Đăng nhập bằng email/password. Gọi API EduApp. Trả kèm cờ bắt buộc đổi mật khẩu (tài khoản GV bulk-import kèm mật khẩu tạm — Phase 5.4). */
export const login = async (username: string, password: string): Promise<{ token: string; requiresPasswordChange: boolean }> => {
    const body: Record<string, unknown> = { email: username, password };
    const ref = (() => { try { return getReferralCode(); } catch { return null; } })();
    if (ref) body.referral_code = ref;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/login`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Đăng nhập thất bại. Vui lòng kiểm tra lại thông tin.');
    }
    const data = await res.json();
    const token = data.access_token as string;
    setEduappAccessToken(token);
    return { token, requiresPasswordChange: !!data.requiresPasswordChange };
};

/** Đổi mật khẩu — dùng cho tài khoản GV được bulk-import kèm mật khẩu tạm, bắt buộc đổi ở lần đăng nhập đầu (Phase 5.4). */
export const changePasswordApi = async (currentPassword: string, newPassword: string): Promise<{ changed: boolean; error?: string }> => {
    if (APP_MODE !== 'PRODUCTION') return { changed: true };
    try {
        const res = await apiFetch(`${API_EDU_PREFIX}/auth/change-password`, {
            method: 'POST',
            body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { changed: false, error: (data as any)?.message || 'Không thể đổi mật khẩu.' };
        return { changed: true };
    } catch (e: any) {
        return { changed: false, error: e?.message || 'Lỗi khi đổi mật khẩu.' };
    }
};

const REFERRAL_STORAGE_KEY = 'EDUAPP_REFERRAL_CODE';
const REFERRAL_META_STORAGE_KEY = 'EDUAPP_REFERRAL_META';
const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface StoredReferralMeta {
    code: string;
    capturedAt: number;
    expiresAt: number;
    landingPath?: string;
}

const readReferralMeta = (): StoredReferralMeta | null => {
    try {
        const raw = localStorage.getItem(REFERRAL_META_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredReferralMeta;
        if (!parsed?.code || !parsed?.expiresAt) return null;
        if (Date.now() > parsed.expiresAt) {
            localStorage.removeItem(REFERRAL_META_STORAGE_KEY);
            localStorage.removeItem(REFERRAL_STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch { return null; }
};

const writeReferralMeta = (code: string) => {
    const now = Date.now();
    const meta: StoredReferralMeta = {
        code,
        capturedAt: now,
        expiresAt: now + REFERRAL_TTL_MS,
        landingPath: typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : undefined,
    };
    try {
        localStorage.setItem(REFERRAL_STORAGE_KEY, code);
        localStorage.setItem(REFERRAL_META_STORAGE_KEY, JSON.stringify(meta));
    } catch {}
};

/**
 * Đọc mã giới thiệu từ URL (?ref=CODE hoặc ?referral=CODE) và lưu vào localStorage với TTL 30 ngày.
 * An toàn để gọi ở cấp toàn ứng dụng (App.tsx) — chỉ ghi đè khi có mã mới hợp lệ trên URL.
 * Trả về mã hiện đang hợp lệ (mới capture được hoặc đang lưu trữ).
 */
export const captureReferralFromUrl = (): string | null => {
    try {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('ref') || params.get('referral');
        if (raw && raw.trim().length >= 4) {
            writeReferralMeta(raw.trim());
            return raw.trim();
        }
        const existing = readReferralMeta();
        if (existing) return existing.code;
        const legacy = localStorage.getItem(REFERRAL_STORAGE_KEY);
        if (legacy && legacy.length >= 4) {
            writeReferralMeta(legacy);
            return legacy;
        }
        return null;
    } catch { return null; }
};

/** Xóa mã giới thiệu đã lưu (sau khi đăng ký thành công). */
export const clearReferralCode = () => {
    try {
        localStorage.removeItem(REFERRAL_STORAGE_KEY);
        localStorage.removeItem(REFERRAL_META_STORAGE_KEY);
    } catch {}
};

/** Lấy mã giới thiệu từ URL hoặc localStorage (đã lọc TTL). */
export const getReferralCode = (): string | null => {
    captureReferralFromUrl();
    const meta = readReferralMeta();
    return meta?.code ?? null;
};

/** Lấy thông tin metadata của referral đang lưu (thời điểm capture, landing path...). */
export const getReferralMeta = (): StoredReferralMeta | null => readReferralMeta();

/** Đăng nhập bằng Google id_token (từ GSI / One Tap). Gọi API EduApp. */
export const loginWithGoogleIdToken = async (idToken: string, referralCode?: string | null): Promise<string> => {
    const body: Record<string, unknown> = { google_id_token: idToken };
    if (referralCode) body.referral_code = referralCode;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/google-login`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Đăng nhập Google thất bại.');
    }
    const data = await res.json();
    const token = data.access_token as string;
    setEduappAccessToken(token);
    return token;
};

/** Đăng nhập bằng Google access_token (từ Token Client với scope birthday). Lấy được DOB từ People API. */
export const loginWithGoogleAccessToken = async (accessToken: string, referralCode?: string | null): Promise<string> => {
    const body: Record<string, unknown> = { google_access_token: accessToken };
    if (referralCode) body.referral_code = referralCode;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/google-login`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || 'Đăng nhập Google thất bại.');
    }
    const data = await res.json();
    const token = data.access_token as string;
    setEduappAccessToken(token);
    return token;
};

export const loginWithGoogle = async (googleId: string, googleAccessToken: string, referralCode?: string | null): Promise<string> => {
    const body: Record<string, unknown> = { google_id: googleId, google_token: googleAccessToken };
    if (referralCode) body.referral_code = referralCode;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/google-login`, {
        method: 'POST',
        headers: {},
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Google login failed');
    const data = await res.json();
    const token = data.access_token as string;
    setEduappAccessToken(token);
    return token;
};

/** Đăng nhập bằng Facebook. Gọi API EduApp. */
export const loginWithFacebook = async (facebookId: string, facebookAccessToken: string, referralCode?: string | null): Promise<string> => {
    const body: Record<string, unknown> = { facebook_id: facebookId, facebook_token: facebookAccessToken };
    if (referralCode) body.referral_code = referralCode;
    const res = await apiFetch(`${API_EDU_PREFIX}/auth/facebook-login`, {
        method: 'POST',
        headers: {},
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Facebook login failed');
    const data = await res.json();
    const token = data.access_token as string;
    setEduappAccessToken(token);
    return token;
};

// ==========================================
// Class Management (PRODUCTION)
// ==========================================
export type ClassDto = {
    id: string|number; 
    name: string; 
    academic_year?: string|null; 
    grade_level?: string|null; 
    class_type: 'physical'|'virtual'|'hybrid'; 
    enrollment_mode?: 'manual'|'open'|'invite_only'|'student_code';
    location?: string;
    invite_code: string;
    invite_expiry_date?: string|null;
    security_question?: string|null;
    is_locked?: boolean;
    teacher_name?: string;
};
type StudentDto = { id: string|number; name: string; student_id_code?: string|null; dob?: string|null };
type AssignmentDto = { id: number; homework_id: number; class_id: number; assigned_date: string; due_date: string|null };

export const findClassByCode = async (code: string): Promise<ClassDto | null> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve(null);
  const codeStr = (typeof code === 'string' ? code : '').replace(/\s/g, '').trim();
  if (!codeStr || codeStr.includes('[object')) return Promise.resolve(null);
  try {
    const res = await apiFetch(`${API_EDU_PREFIX}/class/find/${encodeURIComponent(codeStr)}`, { method: 'GET' });
    if (!res.ok) return null;
    const data = await res.json();
    return data.class as ClassDto;
  } catch {
    return null;
  }
};

export const getClasses = async (): Promise<ClassDto[]> => {
  if (APP_MODE !== 'PRODUCTION') return Promise.resolve([]);
  const res = await apiFetch(`${API_EDU_PREFIX}/class`, { method: 'GET' });
  if (!res.ok) throw new Error('Không tải được danh sách lớp');
  const data = await res.json();
  return data.classes as ClassDto[];
};

export const createClass = async (payload: {
    name: string;
    academic_year?: string;
    grade_level?: string;
    subject?: string;
    category?: 'regular'|'tutoring';
    class_type: 'physical'|'virtual'|'hybrid';
    enrollment_mode?: 'manual'|'open'|'invite_only';
    allow_join_from_outside?: boolean;
    location?: string;
    invite_expiry_date?: string|null;
    security_question?: string|null;
    security_answer?: string|null;
    is_locked?: boolean;
}): Promise<ClassDto> => {
  try {
    const res = await apiFetch(`${API_EDU_PREFIX}/class`, { method: 'POST', body: JSON.stringify(payload) });
    if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `Lỗi máy chủ (${res.status}): Không tạo được lớp`);
    }
    const data = await res.json();
    if (!data.class) throw new Error('Phản hồi từ máy chủ không hợp lệ (thiếu dữ liệu lớp)');
    return data.class as ClassDto;
  } catch (error: any) {
    console.error("createClass API Error:", error);
    throw error;
  }
};

export const updateClassInfo = async (id: string|number, payload: Partial<{
    name: string;
    academic_year: string|null;
    grade_level: string|null;
    subject: string|null;
    category: 'regular'|'tutoring'|null;
    class_type: 'physical'|'virtual'|'hybrid';
    enrollment_mode: 'manual'|'open'|'invite_only';
    allow_join_from_outside: boolean;
    location: string|null;
    invite_expiry_date: string|null;
    security_question: string|null;
    security_answer: string|null;
    is_locked: boolean;
}>): Promise<boolean> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
  if (!res.ok) throw new Error('Không cập nhật được lớp');
  return true;
};

export const deleteClassById = async (id: string|number): Promise<boolean> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Không xóa được lớp');
  return true;
};

/** Lưu danh sách sessions (sổ điểm + điểm danh) của một lớp. */
export const updateClassSessions = async (classId: string|number, sessions: any[]): Promise<boolean> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}`, {
    method: 'PATCH',
    body: JSON.stringify({ sessions }),
  });
  if (!res.ok) throw new Error('Không lưu được dữ liệu buổi học');
  return true;
};

/** Đồng bộ trạng thái "Lớp học Thú cưng" (gán thú/lượt phát biểu/biệt danh/thử thách) lên server. */
export const updateClassPetState = async (classId: string|number, petState: any): Promise<boolean> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}`, {
    method: 'PATCH',
    body: JSON.stringify({ pet_state: petState }),
  });
  if (!res.ok) throw new Error('Không lưu được dữ liệu thú cưng');
  return true;
};

// ─── Kế hoạch năm học (PPCT — CV 5512) ─────────────────────────────────────────

/** Một dòng trong bảng phân phối chương trình. */
export interface YearPlanRow {
  stt: number;
  week: string;          // tuần (vd "1" hoặc "1-2")
  periodRange: string;   // tiết thứ (vd "1-2")
  periodCount: number;   // số tiết
  chapter: string;       // chương/chủ đề
  lessonName: string;    // tên bài/nội dung
  requirements: string;  // yêu cầu cần đạt
  note: string;          // thiết bị/ghi chú
}

/** Khung Kế hoạch giáo dục môn học (Phụ lục I CV 5512). */
export interface YearPlanFrame {
  subjectCharacteristics?: string;
  objectives?: string[];
  timeAllocation?: string;
  equipmentNotes?: string;
}

export interface YearPlanResult {
  frame: YearPlanFrame;
  distribution: YearPlanRow[];
  totalPeriods: number;
  totalWeeks: number;
}

export interface YearPlanGenParams {
  subject: string;
  gradeLevel: string;
  textbookSet?: string;     // ket-noi | chan-troi | canh-dieu | khac
  totalWeeks?: number;
  periodsPerWeek?: number;
  schoolYear?: string;
  note?: string;
}

/** AI sinh Kế hoạch năm học (PPCT) theo môn/lớp/bộ SGK. */
export const generateYearPlan = async (params: YearPlanGenParams): Promise<YearPlanResult> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/generate/year-plan`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    let msg = 'Không tạo được kế hoạch năm. Vui lòng thử lại.';
    try { msg = (await res.json())?.message || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  const data = await res.json();
  await refreshCreditBalance('year-plan');
  return data;
};

// ─── Lịch học tuần cho con (phụ huynh) ───────────────────────────────────────

export interface WeeklyPlanSession {
  id: string;
  time: string;
  subject: string;
  topic: string;
  durationMinutes: number;
  type: 'hoc' | 'nghi';
}

export interface WeeklyPlanDay {
  day: string; // T2..CN
  sessions: WeeklyPlanSession[];
}

export interface WeeklyPlanResult {
  summary: string;
  days: WeeklyPlanDay[];
}

export interface WeeklyPlanParams {
  childName?: string;
  gradeLevel?: string;
  goals: string;
  subjectsContext?: string;
}

/** AI sinh lịch học tuần cân đối cho con từ mục tiêu học tập phụ huynh nhập. */
export const generateWeeklyPlan = async (params: WeeklyPlanParams): Promise<WeeklyPlanResult> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/generate/weekly-plan`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    let msg = 'Không tạo được lịch học tuần. Vui lòng thử lại.';
    try { msg = (await res.json())?.message || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  const data = await res.json();
  await refreshCreditBalance('weekly-plan');
  return data;
};

// ─── Biên bản họp tổ chuyên môn ──────────────────────────────────────────────

export interface MeetingAgendaItem {
  topic: string;
  notes?: string;
  conclusion?: string;
}

export interface MeetingMinutesParams {
  schoolName?: string;
  departmentName?: string;
  meetingType?: 'monthly' | 'start_year' | 'before_exam' | 'topic' | 'emergency' | 'regular';
  schoolYear?: string;
  meetingDate?: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  totalMembers?: number;
  presentCount?: number;
  absentees?: string;
  presenter?: string;
  secretary?: string;
  agendaItems?: MeetingAgendaItem[];
  generalNotes?: string;
  /** Phase 6.5: khi có, backend tự điền departmentName/schoolName/totalMembers + số liệu học tập thật của tổ (chỉ điền chỗ chưa gõ). */
  departmentId?: string | number;
}

export interface MeetingMinutesResult {
  agendaItems: { order: number; topic: string; discussion: string; conclusion: string }[];
  generalConclusion: string;
  assignments: { person: string; task: string; deadline: string }[];
  closing: string;
}

export const generateMeetingMinutes = async (params: MeetingMinutesParams): Promise<MeetingMinutesResult> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/generate/meeting-minutes`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    let msg = 'Không tạo được biên bản. Vui lòng thử lại.';
    let body: any = {};
    try { body = await res.json(); msg = body?.message || msg; } catch { /* ignore */ }
    const err: any = new Error(msg);
    // Đính kèm status/errorCode để caller kiểm tra CÓ CẤU TRÚC thay vì dò chuỗi
    // message (vốn có thể đổi wording theo pricing_enforcement_mode — xem GenerateController::insufficientCreditsResponse).
    err.status = res.status;
    err.errorCode = body?.error_code;
    throw err;
  }
  const data = await res.json();
  await refreshCreditBalance('meeting-minutes');
  return data;
};

// ─── Hồ sơ chuyên môn số ─────────────────────────────────────────────────────

export interface PortfolioStats {
  lessonPlans?: number;
  quizzes?: number;
  homeworks?: number;
  activities?: number;
  skknProjects?: number;
  yearPlans?: number;
  classes?: number;
  documents?: number;
  presentations?: number;
  subjects?: string;
  name?: string;
  /** Phase 6.5: có khi gọi ở chế độ TỔ (department_id) thay vì cá nhân. */
  scope?: 'personal' | 'department';
  schoolName?: string;
  memberCount?: number;
  academic?: { gradedCount: number; avgGrade: number | null; lowGradeCount: number };
}

export interface PortfolioNarrativeParams {
  teacherName?: string;
  schoolName?: string;
  departmentName?: string;
  schoolYear?: string;
  documentType?: 'plan' | 'summary';
  stats?: PortfolioStats;
  additionalContext?: string;
}

export interface PortfolioNarrativeResult {
  documentType: 'plan' | 'summary';
  narrative: string;
  goals: string[];
  activities: string[];
  professional: string;
  emulation: string;
  limitations?: string[];
}

/** Phase 6.5: truyền departmentId để lấy số liệu TỔNG HỢP CẢ TỔ (chỉ tổ trưởng/admin trường) thay vì cá nhân. */
/**
 * Phase 6.5: lấy trước schoolName/departmentName/totalMembers của tổ để hiện
 * sẵn trong form Biên bản họp tổ — không có bước này thì màn kết quả/Word xuất
 * ra sẽ trống các field này dù backend vẫn dùng đúng số liệu thật để sinh nội
 * dung (phát hiện khi test E2E qua UI thật, xem docs/tinh-nang/TO_CHUYEN_MON.md).
 */
export const getDepartmentMeetingContext = async (departmentId: string | number): Promise<{ schoolName: string | null; departmentName: string; totalMembers: number } | null> => {
  try {
    const res = await apiFetch(`${API_EDU_PREFIX}/teacher/department-meeting-context?department_id=${departmentId}`);
    if (!res.ok) return null;
    return res.json();
  } catch { return null; }
};

export const getPortfolioSummary = async (departmentId?: string | number): Promise<PortfolioStats> => {
  const url = departmentId
    ? `${API_EDU_PREFIX}/teacher/portfolio-summary?department_id=${departmentId}`
    : `${API_EDU_PREFIX}/teacher/portfolio-summary`;
  const res = await apiFetch(url);
  if (!res.ok) {
    let msg = 'Không lấy được số liệu hồ sơ.';
    try { msg = (await res.json())?.message || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
};

export const generatePortfolioNarrative = async (params: PortfolioNarrativeParams): Promise<PortfolioNarrativeResult> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/generate/portfolio-narrative`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    let msg = 'Không tạo được văn bản. Vui lòng thử lại.';
    let body: any = {};
    try { body = await res.json(); msg = body?.message || msg; } catch { /* ignore */ }
    const err: any = new Error(msg);
    err.status = res.status;
    err.errorCode = body?.error_code;
    throw err;
  }
  const data = await res.json();
  await refreshCreditBalance('portfolio-narrative');
  return data;
};

/** AI sinh nhận xét học sinh gửi phụ huynh. */
export interface ParentReportResult {
  comment: string;
  strengths?: string[];
  improvements?: string[];
  /** Mức gợi ý theo Thông tư (HTT/HT/CHT hoặc Tốt/Khá/Đạt/Chưa đạt) — chỉ gợi ý */
  suggestedLevel?: string;
  /** "Thông tư 27/2020/TT-BGDĐT" | "Thông tư 22/2021/TT-BGDĐT" | "" */
  circular?: string;
}

export const generateParentReport = async (payload: {
  studentName: string;
  className: string;
  gradeSummary: string;
  attendanceSummary: string;
  assignmentSummary?: string;
  /** Cấp học → quyết định khung Thông tư 27 (Tiểu học) / 22 (THCS,THPT) */
  gradeLevel?: string;
  /** 'hoc_ba' = nhận xét học bạ trang trọng · 'parent_message' = tin nhắn PH (mặc định) */
  reportType?: 'hoc_ba' | 'parent_message';
  subject?: string;
  period?: string;
  conductSummary?: string;
}): Promise<ParentReportResult> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/generate/parent-report`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Không tạo được nhận xét');
  return res.json();
};

// ═══════════════════════════════════════════════════════════════════════════
// LIVE QUIZ — quiz đồng bộ realtime kiểu Kahoot (host + player)
// ═══════════════════════════════════════════════════════════════════════════

export interface LiveQuizQuestionInput { text: string; options: string[]; correctIndex: number; }
export type LiveQuizJoinMode = 'free' | 'roster' | 'roster_open';
export interface LiveQuizHostInfo { pin: string; sessionId: number; quizTitle: string; total: number; settings: { questionTime: number; pointsBase: number }; joinMode: LiveQuizJoinMode; }
export interface LiveLeaderboardEntry {
  nickname: string; score: number; streak?: number;
  // Chỉ Quiz Thẻ Giấy trả về — số câu đúng thực tế + điểm chuẩn thang 10 (đúng
  // cách chấm thi thật: số câu đúng/tổng số câu × 10), không phải điểm nội bộ
  correctCount?: number; totalQuestions?: number; grade?: number;
}
export interface LiveQuizRosterStudent { id: string | number; name: string; taken: boolean; }

/** HOST: tạo phòng từ snapshot câu hỏi (chỉ MCQ). Trả về PIN. */
export const hostLiveQuiz = async (payload: {
  quizId?: string; quizTitle?: string;
  questions: LiveQuizQuestionInput[];
  settings?: { questionTime?: number; pointsBase?: number };
  joinMode?: LiveQuizJoinMode;
  classId?: string | number;
  allowedStudentIds?: (string | number)[];
}): Promise<LiveQuizHostInfo> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/live-quiz/host`, { method: 'POST', body: JSON.stringify(payload) });
  if (!res.ok) throw new Error('Không tạo được phòng Live Quiz');
  return res.json();
};

/** HOST: sửa lại đúng học sinh nếu chọn nhầm tên — chỉ dùng được khi phòng còn ở lobby. */
export const reassignLiveQuizPlayer = async (pin: string, playerId: string | number, newStudentId: string | number): Promise<{ ok: boolean; nickname: string }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/live-quiz/${pin}/reassign`, { method: 'POST', body: JSON.stringify({ playerId, newStudentId }) });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không sửa được tên');
  }
  return res.json();
};

/** HOST: trạng thái phòng (lobby, người chơi, tiến độ). */
export const liveQuizHostState = async (pin: string): Promise<any> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/live-quiz/${pin}/state`, { method: 'GET' });
  if (!res.ok) throw new Error('Không tải được trạng thái phòng');
  return res.json();
};

/** HOST: đẩy câu hỏi kế tiếp (hoặc câu chỉ định). */
export const pushLiveQuestion = async (pin: string, index?: number): Promise<any> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/live-quiz/${pin}/push`, { method: 'POST', body: JSON.stringify(index != null ? { index } : {}) });
  if (!res.ok) throw new Error('Không đẩy được câu hỏi');
  return res.json();
};

/** HOST: khoá câu & công bố đáp án + bảng xếp hạng. */
export const revealLiveQuestion = async (pin: string): Promise<any> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/live-quiz/${pin}/reveal`, { method: 'POST' });
  if (!res.ok) throw new Error('Không công bố được đáp án');
  return res.json();
};

/** HOST: kết thúc phiên. */
export const endLiveQuiz = async (pin: string): Promise<any> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/live-quiz/${pin}/end`, { method: 'POST' });
  if (!res.ok) throw new Error('Không kết thúc được phiên');
  return res.json();
};

/** PLAYER: lấy chế độ tham gia của phòng + danh sách lớp (nếu có) trước khi hiện bước nhập tên. */
export const getLiveQuizRoster = async (pin: string): Promise<{ joinMode: LiveQuizJoinMode; students?: LiveQuizRosterStudent[] }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/live-quiz/${pin}/roster`, { method: 'GET' });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không tải được thông tin phòng');
  }
  return res.json();
};

/** PLAYER: nhập PIN + biệt danh (chế độ free/khách) hoặc chọn tên trong danh sách lớp (chế độ roster) để vào phòng. */
export const joinLiveQuiz = async (pin: string, params: { nickname?: string; studentId?: string | number }): Promise<{ playerToken: string; nickname: string; quizTitle: string; status: string; count: number }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/live-quiz/${pin}/join`, { method: 'POST', body: JSON.stringify(params) });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không vào được phòng');
  }
  return res.json();
};

/** PLAYER: nộp đáp án — server chấm điểm, trả kết quả riêng của mình. */
export const answerLiveQuiz = async (pin: string, payload: { playerToken: string; index: number; optionIndex: number }): Promise<{ received: boolean; correct: boolean; points: number; score: number; streak: number }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/live-quiz/${pin}/answer`, { method: 'POST', body: JSON.stringify(payload) });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không gửi được đáp án');
  }
  return res.json();
};

/** PLAYER: trạng thái cá nhân (reconnect). */
export const liveQuizPlayerState = async (pin: string, token: string): Promise<any> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/live-quiz/${pin}/me?token=${encodeURIComponent(token)}`, { method: 'GET' });
  if (!res.ok) throw new Error('Không tải được trạng thái người chơi');
  return res.json();
};

// ═══════════════════════════════════════════════════════════════════════════
// QUIZ THẺ GIẤY (QR Card Quiz) — học sinh dùng thẻ giấy in QR, không cần điện thoại
// ═══════════════════════════════════════════════════════════════════════════

export interface QrCardQuizQuestionInput { text: string; options: string[]; correctIndex: number; }
export interface QrCardQuizSettings {
  pointsBase: number; comboBonus: number; comboThreshold: number;
  autoAdvance: boolean; cardSetSize: number;
  timeLimitSec: number | null;
  revealMode: 'per_question' | 'at_end';
}
export interface QrCardQuizReviewItem {
  index: number; text: string; options: string[]; correctIndex: number; tally: number[];
}
export interface QrCardQuizParticipantDto {
  id: number; studentId: number; name: string; cardNumber: number | null;
  score: number; streak: number;
  optionIndex: number | null; source: 'scan' | 'manual' | 'none' | null;
  correct: boolean | null;
}
export interface QrCardQuizProgress { scannedCount: number; totalParticipants: number; allScanned: boolean; }
export interface QrCardQuizHostInfo extends QrCardQuizProgress {
  pin: string; sessionId: number; quizTitle: string; total: number;
  settings: QrCardQuizSettings; classId: number; participants: QrCardQuizParticipantDto[];
}

/** Tạo phiên Quiz Thẻ Giấy từ snapshot câu hỏi + lớp tham gia. */
export const hostQrCardQuiz = async (payload: {
  quizId?: string; quizTitle?: string;
  questions: QrCardQuizQuestionInput[];
  classId: string | number;
  allowedStudentIds?: (string | number)[];
  settings?: Partial<QrCardQuizSettings>;
}): Promise<QrCardQuizHostInfo> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/host`, { method: 'POST', body: JSON.stringify(payload) });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không tạo được phiên Quiz Thẻ Giấy');
  }
  return res.json();
};

/** Gán số thẻ giấy cho từng học sinh (bước "gán thẻ" đầu phiên). */
export const assignQrCardQuizCards = async (
  pin: string, mode: 'sequential' | 'random' = 'sequential', cardSetSize?: number,
): Promise<{ ok: boolean; participants: QrCardQuizParticipantDto[] }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/${pin}/assign-cards`, {
    method: 'POST', body: JSON.stringify(cardSetSize != null ? { mode, cardSetSize } : { mode }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không gán được thẻ');
  }
  return res.json();
};

/**
 * Sửa số thẻ của 1 học sinh NGAY TRONG PHIÊN — dùng được bất cứ lúc nào phiên
 * chưa kết thúc (khác assignQrCardQuizCards chỉ dùng được ở lobby). Đồng thời
 * cập nhật luôn thẻ mặc định của lớp để lần sau vẫn nhớ đúng số này.
 */
export const reassignQrCardQuizCard = async (
  pin: string, studentId: string | number, cardNumber: number,
): Promise<{ ok: boolean; participants: QrCardQuizParticipantDto[] }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/${pin}/reassign-card`, {
    method: 'POST', body: JSON.stringify({ studentId, cardNumber }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không sửa được số thẻ');
  }
  return res.json();
};

/** Trạng thái đầy đủ của phiên (dùng cho màn quét & đồng bộ lại khi mất kết nối). */
export const qrCardQuizHostState = async (pin: string): Promise<any> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/${pin}/state`, { method: 'GET' });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không tải được trạng thái phiên');
  }
  return res.json();
};

/** Đẩy câu hỏi kế tiếp (hoặc câu chỉ định). */
export const pushQrCardQuizQuestion = async (pin: string, index?: number): Promise<any> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/${pin}/push`, {
    method: 'POST', body: JSON.stringify(index != null ? { index } : {}),
  });
  if (!res.ok) throw new Error('Không đẩy được câu hỏi');
  return res.json();
};

/** Ghi nhận đáp án quét được từ 1 thẻ (số thẻ + đáp án suy ra từ góc xoay). */
export const scanQrCardQuizCard = async (
  pin: string, cardNumber: number, optionIndex: number,
): Promise<{ ok?: boolean; unchanged?: boolean; unassigned?: boolean; message?: string } & Partial<QrCardQuizProgress> & Record<string, any>> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/${pin}/scan`, {
    method: 'POST', body: JSON.stringify({ cardNumber, optionIndex }),
  });
  const body = await res.json().catch(() => ({}));
  // 404 = thẻ lạ/chưa gán: KHÔNG ném lỗi, để vòng quét chạy tiếp, chỉ báo nhẹ
  if (res.status === 404) return { ...body, unassigned: true };
  if (!res.ok) throw new Error(body?.message ?? 'Không ghi nhận được đáp án');
  return body;
};

/** Giáo viên sửa/nhập tay đáp án cho 1 học sinh (optionIndex = null để xoá). */
export const manualAnswerQrCardQuiz = async (
  pin: string, participantId: number, optionIndex: number | null,
): Promise<any> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/${pin}/manual-answer`, {
    method: 'POST', body: JSON.stringify({ participantId, optionIndex }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không sửa được đáp án');
  }
  return res.json();
};

/** Khoá câu hiện tại → chấm điểm + công bố đáp án, thống kê, xếp hạng. */
export const revealQrCardQuizQuestion = async (pin: string): Promise<any> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/${pin}/reveal`, { method: 'POST' });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không công bố được đáp án');
  }
  return res.json();
};

/** Kết thúc phiên. */
export const endQrCardQuiz = async (pin: string): Promise<any> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/${pin}/end`, { method: 'POST' });
  if (!res.ok) throw new Error('Không kết thúc được phiên');
  return res.json();
};

// ── Thẻ mặc định theo lớp (độc lập với phiên) ──────────────────────────────

export interface QrCardQuizClassCardDto { id: string; name: string; cardNumber: number | null; }

/** Danh sách học sinh trong lớp kèm số thẻ mặc định (null nếu chưa gán). */
export const getQrCardQuizClassCards = async (classId: string | number): Promise<QrCardQuizClassCardDto[]> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/class/${classId}/cards`, { method: 'GET' });
  if (!res.ok) throw new Error('Không tải được danh sách thẻ của lớp');
  const data = await res.json();
  return data.students as QrCardQuizClassCardDto[];
};

/** Đặt/sửa số thẻ mặc định của 1 học sinh trong lớp. */
export const setQrCardQuizClassCard = async (
  classId: string | number, studentId: string | number, cardNumber: number,
): Promise<{ ok: boolean; studentId: string; cardNumber: number }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/class/${classId}/cards/${studentId}`, {
    method: 'PUT', body: JSON.stringify({ cardNumber }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không lưu được số thẻ');
  }
  return res.json();
};

/** Xoá thẻ mặc định của 1 học sinh — lần tạo phiên sau sẽ tự gán số mới. */
export const clearQrCardQuizClassCard = async (classId: string | number, studentId: string | number): Promise<{ ok: boolean }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/class/${classId}/cards/${studentId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Không xoá được số thẻ');
  return res.json();
};

/**
 * Gán tự động thẻ mặc định cho cả lớp.
 * overwrite=false (mặc định): chỉ điền cho học sinh CHƯA có thẻ mặc định.
 * overwrite=true: xoá hết & gán lại toàn bộ theo mode.
 */
export const generateQrCardQuizClassCards = async (
  classId: string | number, mode: 'sequential' | 'random' = 'sequential', overwrite = false,
): Promise<{ ok: boolean; cards: { studentId: string; cardNumber: number | null }[] }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/qr-card-quiz/class/${classId}/cards/generate`, {
    method: 'POST', body: JSON.stringify({ mode, overwrite }),
  });
  if (!res.ok) {
    const msg = await res.json().catch(() => ({}));
    throw new Error(msg?.message ?? 'Không gán được thẻ mặc định');
  }
  return res.json();
};

export const getClassStudents = async (classId: string|number, inviteCode?: string): Promise<StudentDto[]> => {
  const url = inviteCode ? `${API_EDU_PREFIX}/class/${classId}/students?invite_code=${inviteCode}` : `${API_EDU_PREFIX}/class/${classId}/students`;
  const res = await apiFetch(url, { method: 'GET' });
  if (!res.ok) throw new Error('Không tải được học sinh của lớp');
  const data = await res.json();
  return data.students as StudentDto[];
};

export const addStudentToClassApi = async (classId: string|number, payload: { student_id?: number; name?: string; real_name?: string; dob?: string; school_name?: string; student_id_code?: string; parents?: any[] }): Promise<{ added: boolean; student: StudentDto }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students`, { method: 'POST', body: JSON.stringify(payload) });
  if (!res.ok) throw new Error('Không thêm được học sinh');
  return await res.json();
};

export const addMultipleStudentsApi = async (classId: string|number, studentData: {
    name: string;
    student_id_code?: string;
    dob?: string;
    parent_name?: string;
    parent_phone?: string;
    parent_relationship?: string;
    parent_name_2?: string;
    parent_phone_2?: string;
    parent_relationship_2?: string;
}[]): Promise<{ added: number; students: StudentDto[] }> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students/bulk`, { 
    method: 'POST', 
    body: JSON.stringify({ students: studentData }) 
  });
  if (!res.ok) throw new Error('Không thêm được danh sách học sinh');
  return await res.json();
};

export const removeStudentFromClassApi = async (classId: string|number, studentId: string|number): Promise<boolean> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students/${studentId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Không xóa học sinh khỏi lớp');
  return true;
};

export const analyzePronunciationApi = async (audioBase64: string, phrase: string, mimeType: string = 'audio/webm'): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/generate/pronunciation-analysis`, {
        method: 'POST',
        body: JSON.stringify({ audio: audioBase64, phrase, mimeType })
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || 'Lỗi phân tích phát âm');
    }
    const data = await res.json();
    await refreshCreditBalance('pronunciation-analysis');
    return data;
};

export const textToSpeechApi = async (text: string, voice?: string): Promise<{ url: string; size: number; path: string }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/generate/text-to-speech`, {
        method: 'POST',
        body: JSON.stringify({ text, voice })
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || 'Lỗi chuyển văn bản thành giọng nói');
    }
    const data = await res.json();
    await refreshCreditBalance('text-to-speech');
    return data;
};

export const regenerateStudentLoginCodeApi = async (classId: string|number, studentId: string|number): Promise<{ login_code: string; student: StudentDto }> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/students/${studentId}/regenerate-login-code`, { method: 'POST' });
  if (!res.ok) throw new Error('Không thể sinh mã mới');
  return await res.json();
};

/**
 * --- Oral Quiz APIs ---
 */
export const generateOralQuiz = async (
    gradeLevel: GradeLevel,
    subject: string,
    topic: string,
    detailedInstruction: string,
    contextContent: string | null,
    allowAiAugmentation: boolean,
    studentCount: number,
    questionsPerStudent: number,
    questionTypes: string[] = ['ShortAnswer'],
    questionTypeWeights: Record<string, number> = {},
    aiProvider: AIProvider = 'gemini',
    id?: string | number,
    extraOptions?: { sourceDocumentId?: string | number }
): Promise<any> => {
    const payload = { gradeLevel, subject, topic, detailedInstruction, contextContent, allowAiAugmentation, studentCount, questionsPerStudent, questionTypes, questionTypeWeights, aiProvider, id, ...extraOptions };
    try {
        const response = await apiFetch(`${API_EDU_PREFIX}/generate/oral-quiz`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('Lỗi từ máy chủ khi tạo nội dung kiểm tra kiến thức.');
        const data = await response.json();
        await refreshCreditBalance('oral-quiz');
        return data;
    } catch (error) {
        throw handleGeminiError(error, "tạo nội dung kiểm tra kiến thức");
    }
};

export const saveOralQuiz = async (quiz: any): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/oral-quizzes`, {
        method: 'POST',
        body: JSON.stringify(quiz)
    });
    if (!res.ok) throw new Error('Không lưu được nội dung kiểm tra kiến thức');
    const data = await res.json();
    return data.quiz || data;
};

export const generateAiEthicsScenariosApi = async (topic: string, content: string = ''): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/generate/ai-ethics-scenarios`, {
        method: 'POST',
        body: JSON.stringify({ topic, content })
    });
    if (!res.ok) throw new Error('Không tạo được thử thách đạo đức AI');
    return await res.json();
};

export const generateAiFactCheckAnalysisApi = async (claim: string, content: string = ''): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/generate/ai-fact-check`, {
        method: 'POST',
        body: JSON.stringify({ claim, content })
    });
    if (!res.ok) throw new Error('Không thực hiện được kiểm chứng AI');
    return await res.json();
};

export const startOralQuizSession = async (oralQuizId: string | number, classId: string | number, selectedStudentIds: string[]): Promise<any> => {
    const res = await apiFetch(`${API_EDU_PREFIX}/oral-quiz-sessions/start`, {
        method: 'POST',
        body: JSON.stringify({ oral_quiz_id: oralQuizId, class_id: classId, selected_student_ids: selectedStudentIds })
    });
    if (!res.ok) throw new Error('Không khởi tạo được phiên kiểm tra');
    return await res.json();
};

export const updateOralQuizSession = async (sessionId: string | number, data: any): Promise<any> => {
    // Map camelCase to snake_case for backend
    const payload: Record<string, any> = {};
    if (data.currentStudentId !== undefined) payload.current_student_id = data.currentStudentId;
    if (data.results !== undefined) payload.results = data.results;
    if (data.status !== undefined) payload.status = data.status;

    const res = await apiFetch(`${API_EDU_PREFIX}/oral-quiz-sessions/${sessionId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error('Không cập nhật được phiên kiểm tra');
    return await res.json();
};

export const listAssignmentsByClass = async (classId: string|number): Promise<AssignmentDto[]> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/assignments`, { method: 'GET' });
  if (!res.ok) throw new Error('Không tải được danh sách giao bài');
  const data = await res.json();
  return data.assignments as AssignmentDto[];
};

export const assignHomeworkToClass = async (classId: string|number, homeworkId: number, dueDate: string): Promise<AssignmentDto> => {
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/assignments`, { method: 'POST', body: JSON.stringify({ homework_id: homeworkId, due_date: dueDate, type: 'homework' }) });
  if (!res.ok) throw new Error('Không giao bài tập');
  const data = await res.json();
  return data.assignment as AssignmentDto;
};

export type CreateAssignmentPayload = {
  type: 'homework' | 'task' | 'announcement';
  homework_id?: number | null;
  title?: string;
  due_date?: string;
  instructions?: string;
  student_ids?: string[];
  scheduled_date?: string;
  passing_grade?: number;
  is_required?: boolean;
  /** Snapshot sơ đồ tư duy đính kèm (xem features/mindmap/types MindmapAttachment) */
  mindmaps?: any[];
};

export const createAssignment = async (classId: string|number, payload: CreateAssignmentPayload): Promise<{ id: number; [k: string]: any }> => {
  const body: Record<string, any> = {
    type: payload.type,
    title: payload.title,
    due_date: payload.due_date,
    instructions: payload.instructions,
    student_ids: payload.student_ids,
    scheduled_date: payload.scheduled_date,
    passing_grade: payload.passing_grade,
    is_required: payload.is_required,
  };
  if (payload.mindmaps && payload.mindmaps.length) {
    body.mindmaps = payload.mindmaps;
  }
  if (payload.type === 'homework' && payload.homework_id) {
    body.homework_id = payload.homework_id;
  } else if (payload.homework_id) {
    body.homework_id = payload.homework_id;
  }
  const res = await apiFetch(`${API_EDU_PREFIX}/class/${classId}/assignments`, { method: 'POST', body: JSON.stringify(body) });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || 'Không giao bài tập');
  }
  const data = await res.json();
  return data.assignment;
};

export const getVideoDownloadUrl = (video: VideoResource): string => {
  if (APP_MODE !== 'PRODUCTION' || !video.id || video.id.toString().startsWith('id_')) {
    return video.uri;
  }
  // Trả về path tương đối để apiFetch có thể xử lý (thêm base url và auth token)
  return `${API_EDU_PREFIX}/video-resources/${video.id}/download`;
};

/**
 * Generic API Service object for general requests.
 */
export const apiService = {
    get: async (path: string) => {
        const res = await apiFetch(path, { method: 'GET' });
        if (!res.ok) {
            // Đọc message từ body để FE có thể hiển thị toast thân thiện thay vì status code khô khan
            const err = await res.json().catch(() => ({ error: 'Request failed' }));
            throw new Error(err.error || err.message || `GET ${path} failed: ${res.status}`);
        }
        return res.json();
    },
    post: async (path: string, body: any) => {
        const res = await apiFetch(path, {
            method: 'POST',
            body: body ? JSON.stringify(body) : undefined
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Request failed' }));
            throw new Error(err.error || err.message || `POST ${path} failed: ${res.status}`);
        }
        return res.json();
    },
    patch: async (path: string, body: any) => {
        const res = await apiFetch(path, {
            method: 'PATCH',
            body: body ? JSON.stringify(body) : undefined
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Request failed' }));
            throw new Error(err.error || err.message || `PATCH ${path} failed: ${res.status}`);
        }
        return res.json();
    },
    delete: async (path: string) => {
        const res = await apiFetch(path, { method: 'DELETE' });
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Request failed' }));
            throw new Error(err.error || err.message || `DELETE ${path} failed: ${res.status}`);
        }
        return res.json();
    }
};
